"use client";

import type { Attachment, UIMessage } from "ai";
import { useChat } from "@ai-sdk/react";
import { useEffect, useLayoutEffect, useState, useRef, useMemo, useCallback } from "react";
import useSWR, { useSWRConfig } from "swr";
import { ChatHeader } from "@/components/chat-header";
import { AlertTopBar } from "@/components/alert-page/AlertTopBar";
import type { Vote } from "@/lib/api-client";
import { API_BASE_URL } from "@/lib/config";
import {
  findKeywordReplayRoute,
  readKeywordReplayRoutingEnabled,
  useDemoReplayInputEnabled,
} from "@/lib/demo-replay-config";
import { cn, fetchWithErrorHandlers, generateUUID } from "@/lib/utils";
import { useChatVisibility } from "@/hooks/use-chat-visibility";
import { Artifact } from "./artifact";
import { Messages } from "./messages";
import type { VisibilityType } from "./visibility-selector";
import { useArtifactSelector, useArtifact } from "@/hooks/use-artifact";
import { getChatHistoryPaginationKey } from "./sidebar-history";
import { toast } from "./toast";
import { MultimodalInput } from "./multimodal-input";
import { useAutoResume } from "@/hooks/use-auto-resume";
import { telemetry } from "@/lib/telemetry";
import { sessionFetcher } from "@/lib/api-client";
import { convertToUIMessages } from "@/lib/session-utils";
import type { PageContextPayload } from "@/lib/page-context-types";
import { useConsultationEvents } from "@/hooks/use-consultation-events";
import { ChatSDKError } from "@/lib/errors";
import { apiClient } from "@/lib/api-client";
import { useAuth } from "@/contexts/AuthContext";
import { useUserUsage } from "@/hooks/useUserUsage";
import { usageService } from "@/services/usageService";
import {
  modelByKey,
  modelKeyFromTier,
  isAgentModelKey,
  SELECTED_MODEL_STORAGE_KEY,
  type AgentModelKey,
} from "@/lib/ai/agent-models";
import { usePathname, useSearchParams, useRouter } from "next/navigation";
import { FastForwardProvider } from "@/contexts/FastForwardContext";
import type { RequestedDataSource } from "@/lib/data-catalog";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { debugLog, createChannelLogger } from "@/lib/debug-log";
import { ArrowLeft } from "lucide-react";
import {
  ONBOARDING_DEMO_CURATED_ID,
  ONBOARDING_REPLAY_SPEED,
  CLEAN_REPLAY_SPEED,
  ONBOARDING_ASK_ALERT_EVENT,
  type OnboardingAskAlertDetail,
} from "@/lib/onboarding-constants";
import { dispatchStageComplete } from "@/lib/onboarding-tour";

const timingLog = createChannelLogger("alert_timing");
// Dedicated channel for the main-chat → alert-page edit_alert handoff flow.
// Writes to frontend_edit_alert_handoff.txt. Paired with the same channel on
// the /alerts/[id] page so both hops land in one file.
const handoffLog = createChannelLogger("edit_alert_handoff");
const prefillLog = createChannelLogger("alerts_onboarding");
// Writes to frontend_replay_debug.txt — used to diagnose the replay
// Continue-button race. Cleared via DELETE /api/debug-log?channel=replay_debug.
const replayLog = createChannelLogger("replay_debug");
function tSinceNav(): number {
  if (typeof performance === "undefined") return -1;
  const navs = performance.getEntriesByType?.("navigation") as PerformanceNavigationTiming[] | undefined;
  const navStart = navs?.[0]?.startTime ?? 0;
  return Math.round(performance.now() - navStart);
}

/**
 * Cheap, non-cryptographic hash for keying sessionStorage anti-replay guards.
 * djb2 variant — fast, stable, and collision-resistant enough for short
 * strings. We only use it to fingerprint a ?q= value so a refresh with the
 * same query doesn't re-submit; collisions would just cause an unrelated
 * query to be skipped, which in practice means one dropped auto-submit,
 * which is harmless.
 */
function cheapHash(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i);
  }
  return (h >>> 0).toString(36);
}

import { Button } from "./ui/button";
import dynamic from "next/dynamic";

const Feed = dynamic(() => import("./feed").then((mod) => mod.Feed), { ssr: false });
const Dither = dynamic(() => import("./Dither"), { ssr: false });
const Footer = dynamic(() => import("./footer").then((mod) => mod.Footer), { ssr: false });
const LovedByRetailSection = dynamic(() => import("./landing/loved-by-retail-section").then((mod) => mod.LovedByRetailSection), { ssr: false });
import { ResearchCostSection } from "./landing/research-cost-section";
const WorkflowSection = dynamic(() => import("./landing/workflow-section").then((mod) => mod.WorkflowSection), { ssr: false });
const LogoWallSection = dynamic(() => import("./landing/logo-wall-section").then((mod) => mod.LogoWallSection), { ssr: false });
const AutomationsSection = dynamic(() => import("./landing/automations-section").then((mod) => mod.AutomationsSection), { ssr: false });
const PricingSection = dynamic(() => import("./landing/pricing-section").then((mod) => mod.PricingSection), { ssr: false });
const CTASection = dynamic(() => import("./landing/cta-section").then((mod) => mod.CTASection), { ssr: false });
const ReplayControls = dynamic(() => import("./replay-controls").then((mod) => mod.ReplayControls), { ssr: false });
const PostReplayInput = dynamic(() => import("./post-replay-input").then((mod) => mod.PostReplayInput), { ssr: false });
const DemoReplayInput = dynamic(() => import("./demo-replay-input").then((mod) => mod.DemoReplayInput), { ssr: false });
// Onboarding buddy overlay for replay tours. Rendered when the backend
// emits a `buddy_beat` SSE data part (see _buddy_beat_payload in
// app/api/routes/replay.py). SSR-disabled because it portals to document.body.
const BuddyOverlay = dynamic(() => import("./buddy/BuddyOverlay").then((mod) => mod.BuddyOverlay), { ssr: false });

// Self-hosted ticker tape (Xynth-owned, talks directly to TradingView's
// public widget data feed). Replaces the third-party <tv-ticker-tape>
// custom-element. Loaded dynamically so the WS / lightweight-charts
// bundle stays out of the initial chat payload.
const XynthTickerTape = dynamic(
  () => import("./ticker-tape/XynthTickerTape").then((m) => m.XynthTickerTape),
  { ssr: false },
);
const TICKER_TAPE_SYMBOLS = [
  "FOREXCOM:SPXUSD",
  "FOREXCOM:NSXUSD",
  "FOREXCOM:DJI",
  "FX:EURUSD",
  "BITSTAMP:BTCUSD",
  "BITSTAMP:ETHUSD",
  "CMCMARKETS:GOLD",
];

function createErrorCardMessage({
  errorMessage,
  errorType,
  errorSubtype,
}: {
  errorMessage: string;
  errorType: string;
  errorSubtype?: string;
}): UIMessage {
  const newMessage: UIMessage = {
    id: generateUUID(),
    role: "assistant",
    parts: [],
    content: "",
    createdAt: new Date(),
    experimental_attachments: [
      {
        name: "error-card",
        url: "error-card",
        contentType: "application/json",
      },
    ],
  };

  (newMessage as any).errorData = {
    errorMessage,
    errorType,
    errorSubtype,
  };

  return newMessage;
}

interface PendingStreamTelemetryMeta {
  requestId: string | null;
  agentType: string;
  modelTier: "basic" | "max";
  modelTierState?: "basic" | "max" | null;
  modelTierSavedPref?: "basic" | "max" | null;
  deepResearch: boolean;
  hasAttachments: boolean;
  messageLen: number;
  isReplay: boolean;
}

interface StreamVisibilityEvent {
  offset_ms: number;
  at_iso: string;
  visibility_state: string;
}

interface StreamNetworkEvent {
  offset_ms: number;
  at_iso: string;
  is_online: boolean;
}

interface LiveStreamTelemetryState {
  requestId: string | null;
  startedAtMs: number | null;
  startedAtIso: string | null;
  lastActivityAtMs: number | null;
  lastChunkId: string | null;
  chunkOffsetsMs: number[];
  heartbeatOffsetsMs: number[];
  dropOffsetsMs: number[];
  visibilityEvents: StreamVisibilityEvent[];
  networkEvents: StreamNetworkEvent[];
  maxGapMs: number;
  meta: Omit<PendingStreamTelemetryMeta, "requestId">;
}

// Metric reclassification (Aug 2026): these subtypes show an intentional
// billing/upgrade card, not a failure. They are emitted as stream.limit_hit
// instead of stream.error so the error rate reflects real dead ends.
const BILLING_WALL_SUBTYPES = new Set([
  "no_active_trial",
  "free_credits_exhausted",
  "gemini_daily_limit",
  "trial_ended",
  "subscription_expired",
  "usage_exceeded",
]);

// Max silent re-submits for failed_to_start (0 chunks ever arrived). Reset on
// every successful stream. 2 attempts because flaky mobile radios often fail
// the first retry too (5/18 single retries still gave_up since Jul 31 launch).
const MAX_AUTO_RETRY_SUBMITS = 2;

function createPendingStreamTelemetryMeta(): PendingStreamTelemetryMeta {
  return {
    requestId: null,
    agentType: "main",
    modelTier: "max",
    deepResearch: false,
    hasAttachments: false,
    messageLen: 0,
    isReplay: false,
  };
}

function createLiveStreamTelemetryState(): LiveStreamTelemetryState {
  return {
    requestId: null,
    startedAtMs: null,
    startedAtIso: null,
    lastActivityAtMs: null,
    lastChunkId: null,
    chunkOffsetsMs: [],
    heartbeatOffsetsMs: [],
    dropOffsetsMs: [],
    visibilityEvents: [],
    networkEvents: [],
    maxGapMs: 0,
    meta: {
      agentType: "main",
      modelTier: "max",
      deepResearch: false,
      hasAttachments: false,
      messageLen: 0,
      isReplay: false,
    },
  };
}

function getLatestRedisCursor(data: any[] | undefined) {
  if (!Array.isArray(data)) return null;

  for (let index = data.length - 1; index >= 0; index -= 1) {
    const item = data[index] as any;
    if (
      item?.type === "redis_chunk_id" &&
      typeof item?.content === "string" &&
      typeof item?.request_id === "string"
    ) {
      return {
        chunkId: item.content,
        requestId: item.request_id,
      };
    }
  }

  return null;
}

export function Chat({
  id,
  initialMessages,
  initialVisibilityType,
  isReadonly,
  readonlyReason,
  autoResume,
  section,
  title,
  initialUserQuery,
  initialPrefill,
  initialModelTier,
  initialModelKey,
  initialSkillRun,
  initialActiveSkill,
  sourceNotebookId,
  replayId,
  replayStartAtBeat,
  replayCleanMode = false,
  hasSubAgents,
  viewMode: initialViewMode,
  alertStartCellIndex: initialAlertStartCellIndex,
  alertDoc,
  alertIsOwner,
  alertIsSubscribed,
  alertSubscribedEmail,
  alertSubscriberCount,
  alertPresentedBy,
  isAlertsRoute = false,
  onAlertMutated,
  onAlertDeleted,
  alertVisitorMode = false,
  alertContextId,
  onVisitorSubmitGate,
}: {
  id: string;
  initialMessages: Array<UIMessage>;
  initialVisibilityType: VisibilityType;
  isReadonly: boolean;
  readonlyReason?: "breaking_change" | "ownership" | "replay";
  autoResume: boolean;
  section?: "chat";
  title?: string;
  initialUserQuery?: string;
  initialPrefill?: string;
  /** Optional URL-forced model tier. Used by alert fork/create handoffs so
   *  the auto-submitted first message uses Pro/Max (Claude) instead of the
   *  default basic/Gemini path. Omitted for normal chats. */
  initialModelTier?: "basic" | "max";
  /** Skill launch: the exact model the skill recommends (or the user picked in
   *  the Run-now modal). Takes precedence over the saved-on-device model for
   *  this one navigation. */
  initialModelKey?: AgentModelKey;
  /** Skill launch: raise the turn cap for the first message so a multi-step
   *  skill never gets cut off (sends `extended_turns: true`). Cleared after
   *  the first request so the rest of the chat behaves normally. */
  initialSkillRun?: boolean;
  /** Skill launch (no-blob): arms the chat bar with an active skill so the
   *  user types their own input and the skill's engineered instruction is
   *  sent as `skill_id` (resolved + injected server-side) instead of pasted
   *  into the visible message. */
  initialActiveSkill?: { id: string; name: string };
  sourceNotebookId?: string;
  replayId?: string;
  /** Optional `beat_id` from the curated session's buddy_script to start
   *  the replay at. Forwarded to `POST /replay/stream` so the backend
   *  skips earlier turns + pre-releases earlier beats. Used by the
   *  onboarding tour hub's "Build your first alert" stage which drops
   *  the user directly at turn 1 (alert building). */
  replayStartAtBeat?: string;
  /** Clean replay mode — same recording, no buddy overlays, no auto-prefill
   *  of the canned next-user-message into the input bar. The viewer types
   *  their own message to advance the replay. Routed to by Cmd+Enter on
   *  the homepage composer. See app/(chat)/replay/[id]/clean/page.tsx. */
  replayCleanMode?: boolean;
  hasSubAgents?: boolean;
  viewMode?: string;
  alertStartCellIndex?: number | null;
  /**
   * When this Chat is rendered for an /alerts/[id] route, pass the alert
   * document so the AlertTopBar (shown above the canvas in artifact-full
   * mode) can render the alert name / schedule / email chips + delete
   * button. Leave undefined for normal /chat/[id] routes.
   */
  alertDoc?: import("@/lib/api-client").AlertDoc;
  /** True when the current user is the owner of `alertDoc`. Gates the
   *  AlertTopBar edit/delete pencils. Non-owners see a read-only top bar. */
  alertIsOwner?: boolean;
  /** True when the non-owner caller has an active subscription to this alert.
   *  Flips the public header's LIVE/DELAYED chip + Subscribe button state. */
  alertIsSubscribed?: boolean;
  /** The email the current caller subscribed with. Shown inside the
   *  "Subscribed" dropdown on the public header. `null` for owners + strangers. */
  alertSubscribedEmail?: string | null;
  /** Count of active subscribers — OWNER ONLY per backend privacy.
   *  Shown in the (i) info popover so the owner has visibility without
   *  leaking it to strangers. Undefined when the viewer is not the owner. */
  alertSubscriberCount?: number;
  /** Public byline payload from `GET /alerts/{id}`. Flows Chat → Artifact →
   *  AlertCanvas → AlertCreatorNode, rendered as a floating "Made by {creator}"
   *  card on the canvas (disconnected from the cell flow). `null` / anonymous
   *  → no byline node is injected. */
  alertPresentedBy?: import("@/lib/api-client").PublicProfile | null;
  /** True when this Chat is mounted on the /alerts/[id] route (the dedicated
   *  alert detail page). Different from `viewMode === "alert"` which just
   *  indicates the session is in alert mode — the /chat/[id] route can ALSO
   *  be in alert mode via switch_mode("alert"). Used downstream to enable
   *  the fancy icon-tab round-robin + last-node right-column layout ONLY on
   *  the real /alerts page, keeping /chat's alert-mode view static & calm. */
  isAlertsRoute?: boolean;
  /** Called when the alert top-bar pencils mutate the alert (rename, email,
   *  schedule edit). Parent should revalidate the alert SWR key. */
  onAlertMutated?: () => void;
  /** Called when the alert is deleted from the top-bar. Parent should do a
   *  hard redirect (xynth.com). */
  onAlertDeleted?: () => void;
  /** True when this Chat is a non-owner VISITOR session on /alerts/[id].
   *  The page passes this for any signed-in non-owner. The chat then
   *  uses its own per-(user, alert) session id (passed as `id`),
   *  starts with empty messages, and forwards `alert_context_id` on
   *  every /agent/stream request so the backend prunes alert-write
   *  tools and injects the visitor system-prompt snippet. */
  alertVisitorMode?: boolean;
  /** The alert id whose visitor chat this is — required when
   *  `alertVisitorMode` is true. Sent verbatim to the backend on every
   *  /agent/stream request as `alert_context_id`. Ignored when not in
   *  visitor mode. */
  alertContextId?: string | null;
  /** Synchronous gate called right before a visitor submits a message.
   *  Return `true` to allow the network call (paid user). Return `false`
   *  to swallow the submit and let the parent open the appropriate
   *  modal (auth modal for signed-out, plans modal for free tier).
   *  Forwarded directly into <Artifact>. Ignored unless
   *  `alertVisitorMode` is true. */
  onVisitorSubmitGate?: () => boolean;
}) {
  const { mutate } = useSWRConfig();
  const { user } = useAuth();
  const { refreshUsage, data: usageData } = useUserUsage();
  const pathname = usePathname();

  // Timing log for alert-page load only (no noise on /chat routes).
  const _chatMountLogged = useRef(false);
  if (alertDoc && !_chatMountLogged.current) {
    _chatMountLogged.current = true;
    timingLog("chat:first_call", { t: tSinceNav(), id });
  }

  const [viewMode, setViewMode] = useState<string>(initialViewMode || "research");
  const [alertStartCellIndex, setAlertStartCellIndex] = useState<number | null>(
    initialAlertStartCellIndex ?? null,
  );

  useEffect(() => {
    setViewMode(initialViewMode || "research");
  }, [initialViewMode]);

  useEffect(() => {
    setAlertStartCellIndex(initialAlertStartCellIndex ?? null);
  }, [initialAlertStartCellIndex]);

  // Non-owner visitors chat under `id` = `alert-visit-{alertId}-{uid}`, but
  // the alert canvas must read PKA cell state from the OWNER's linked session
  // — otherwise active-run-details returns zero cells and the canvas stays on
  // "Waiting for the agent…". Owners keep null here (canvas uses `id` == linked).
  const activeRunSessionId = useMemo(() => {
    if (alertVisitorMode && alertDoc?.linked_session_id) {
      return alertDoc.linked_session_id;
    }
    return null;
  }, [alertVisitorMode, alertDoc?.linked_session_id]);

  const router = useRouter();
  const searchParams = useSearchParams();

  const agentType = searchParams.get("agent") || "main";
  const isMobile = useIsMobile();
  const [showBelowFold, setShowBelowFold] = useState(false);
  const authTokenRef = useRef<string | null>(null);

  const [hasAutoOpenedArtifact, setHasAutoOpenedArtifact] = useState(false);
  const isFirstArtifactCheckRef = useRef(true);

  const { visibilityType }: { visibilityType: VisibilityType } =
    useChatVisibility({
      chatId: id,
      initialVisibilityType,
    });

  const pendingStreamTelemetryRef = useRef<PendingStreamTelemetryMeta>(
    createPendingStreamTelemetryMeta(),
  );
  /** Set by ContextualChatDock right before submit; consumed in prepareRequestBody. */
  const pageContextRef = useRef<PageContextPayload | null>(null);
  const liveStreamTelemetryRef = useRef<LiveStreamTelemetryState>(
    createLiveStreamTelemetryState(),
  );
  const liveStreamAbortSourceRef = useRef<string | null>(null);
  const statusTransitionRef = useRef("ready");
  const streamFetch = useCallback(
    async (input: RequestInfo | URL, init?: RequestInit) => {
      let token = authTokenRef.current;
      if (!token && user) {
        try {
          token = await user.getIdToken();
          authTokenRef.current = token;
        } catch {
          token = null;
        }
      }
      const headers = new Headers(init?.headers || {});
      if (token && !headers.has("Authorization")) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return fetchWithErrorHandlers(input, { ...init, headers });
    },
    [user],
  );

  const {
    messages,
    setMessages,
    setData,
    handleSubmit,
    input,
    setInput,
    append,
    status,
    stop,
    reload,
    data,
  } = useChat({
    api: replayId ? `${API_BASE_URL}/replay/stream` : `${API_BASE_URL}/agent/stream`,
    id: id,
    initialMessages,
    experimental_throttle: 100,
    sendExtraMessageFields: true,
    generateId: generateUUID,
    fetch: streamFetch,
    experimental_prepareRequestBody: (body) => {
      const lastMessage = body.messages.at(-1)?.content || "";

      const dataRequested = requestedDataRef.current ?? [];
      requestedDataRef.current = [];

      const deepResearch = deepResearchRef.current;
      const modelTier = modelTierRef.current;
      const messageId = body.messages.at(-1)?.id;
      const pageCtx = pageContextRef.current ?? undefined;
      // Read-and-clear: extended turns apply to the skill's first message only.
      // Create-a-skill interviews can run long, so always raise the turn cap
      // when it's the armed skill — no matter which entry point armed it
      // (Skills-menu "Create a skill", "Save as skill", or a deep link).
      const extendedTurns =
        skillRunRef.current ||
        (activeSkillIdRef.current?.includes("create-a-skill") ?? false);
      skillRunRef.current = false;
      // Read-and-clear: the active skill id(s) for THIS message only. Sent as
      // `skill_ids`; backend resolves the engineered instruction(s) + injects.
      const skillIds = activeSkillIdRef.current;
      activeSkillIdRef.current = undefined;
      const skillId = skillIds && skillIds.length ? skillIds[0] : undefined;

      pendingStreamTelemetryRef.current = {
        requestId: messageId ?? null,
        agentType: replayId ? "replay" : agentType,
        modelTier: modelTier as "basic" | "max",
        deepResearch,
        hasAttachments:
          (body.messages.at(-1)?.experimental_attachments?.length ?? 0) > 0,
        messageLen: lastMessage.length,
        isReplay: Boolean(replayId),
      };

      const savedPrefTier = usageDataRef.current?.ui_prefs?.model_tier ?? null;
      const stateTier = modelTierStateRef.current;
      debugLog("prepare-body", {
        session_id: id, messageId, deepResearch,
        modelTier_sent: modelTier,
        modelTier_state: stateTier,
        modelTier_savedPref: savedPrefTier,
        modelTier_mismatch: modelTier !== stateTier || (savedPrefTier !== null && savedPrefTier !== modelTier),
        hasAttachments: pendingStreamTelemetryRef.current.hasAttachments,
        attachmentCount: body.messages.at(-1)?.experimental_attachments?.length ?? 0,
        hasPageContext: Boolean(pageCtx),
        pageContext: pageCtx,
        msgLen: lastMessage.length,
        skill_id_sent: skillId || null,
        skill_ids_sent: skillIds || null,
      });
      if (skillIds && skillIds.length) {
        // eslint-disable-next-line no-console
        console.log("[SKILL-LAUNCH] prepare-body sending skill_ids", { skillIds, msgLen: lastMessage.length, session_id: id });
      }
      // eslint-disable-next-line no-console
      pendingStreamTelemetryRef.current.modelTierState = stateTier;
      pendingStreamTelemetryRef.current.modelTierSavedPref = savedPrefTier;

      if (deepResearch && messageId) {
        subAgentMessageIdsRef.current.add(messageId);
      }
      // Record skill(s) -> message id so the artifact card(s) render live
      // (before reload). On reload they come back via message.skillIds from DB.
      if (skillIds && skillIds.length && messageId) {
        messageSkillsRef.current.set(messageId, skillIds);
      }
      if (replayId) {
        const buddyVariant =
          typeof window !== "undefined" &&
          window.matchMedia("(max-width: 767px)").matches
            ? "mobile"
            : "desktop";
        return {
          user_id: user?.uid || "replay_viewer",
          session_id: id,
          message: lastMessage,
          id: body.messages.at(-1)?.id,
          curated_id: replayId,
          buddy_variant: buddyVariant,
          // Only meaningful on the FIRST request for this session_id —
          // the backend ignores it on continuations. Sending it on
          // every turn is harmless and keeps the call site simple.
          start_at_beat: replayStartAtBeat || undefined,
          // Clean replay route (/replay/<id>/clean): tells the backend
          // to load an empty buddy_script so no `buddy_beat` SSE frames
          // ever fire and the stream never busy-waits on a release.
          // Same first-request-only semantics as `start_at_beat`.
          suppress_buddy: replayCleanMode || undefined,
          // Seed the backend's per-session speed at creation time so
          // the very first asyncio.sleep already uses the desired
          // speed. Without this, the recording plays at 1× until the
          // frontend's follow-up `/replay/control` POST lands a few
          // hundred ms in, which is visibly slower for clean mode.
          // Ignored by the backend on continuation requests.
          initial_speed: replayCleanMode ? replaySpeed : undefined,
        };
      }

      pageContextRef.current = null;

      return {
        user_id: user?.uid,
        session_id: id,
        message: lastMessage,
        visibility: visibilityType,
        attachments: body.messages.at(-1)?.experimental_attachments || [],
        id: body.messages.at(-1)?.id,
        data_requested: dataRequested.length > 0 ? dataRequested : undefined,
        source_notebook_id: sourceNotebookId,
        agent_type: agentType,
        deep_research: deepResearch,
        // Skill launch: raise the turn cap for this first message only, then
        // clear so continuation messages behave normally. NO planner / sub-
        // agents (extended_turns, not deep_research) per the no-planning rule.
        extended_turns: extendedTurns || undefined,
        // Skill launch (no-blob): identity only; backend injects the instruction.
        skill_id: skillId || undefined,
        skill_ids: skillIds && skillIds.length ? skillIds : undefined,
        model_tier: modelTier,
        model_override: modelOverrideRef.current,
        page_context: pageCtx,
        // Non-owner visitor sessions + owner alert-page contextual chat.
        alert_context_id:
          alertVisitorMode && alertContextId
            ? alertContextId
            : isAlertsRoute && alertDoc?._id
              ? alertDoc._id
              : undefined,
      };
    },
    onFinish: () => {
      debugLog("stream-finish", {
        session_id: id,
        request_id: liveStreamTelemetryRef.current.requestId,
      });
      // Re-arm the failed_to_start silent retries after any finished stream.
      autoRetrySubmitCountRef.current = 0;
      const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
      const hadBackendError = lastAssistant?.parts?.some((p: any) => {
        if (p.type !== "tool-invocation" || p.toolInvocation?.state !== "result") return false;
        const { toolName } = p.toolInvocation;
        const result = p.toolInvocation.result;
        if (toolName === "error_logging_tool" && result?.status === "error") return true;
        if (result?.status === "canceled" && result?.error_type === "APIError") return true;
        return false;
      });
      if (hadBackendError) {
        finishLiveStream("error", {
          error_type: "backend_api_error",
          error_subtype: "llm_stream_crash",
          error_message: "Agent stream ended with a backend error",
        });
      } else {
        finishLiveStream("complete");
      }
      refreshUsage();
      mutate(`${API_BASE_URL}/notebook/by-session/${id}`);
    },
    onError: (error) => {
      stop();
      debugLog("stream-error", {
        session_id: id,
        request_id: liveStreamTelemetryRef.current.requestId,
        error_type: error instanceof ChatSDKError ? error.type : "unknown",
        error_message: error instanceof Error ? error.message : String(error),
      });

      let errorMessage: string;
      let errorType: string = "unknown";
      let errorSubtype: string | undefined;

      if (error instanceof ChatSDKError) {
        errorType = error.type;
        const cause = (error.cause as string) || "";

        if (error.type === "forbidden" && error.surface === "stream") {
          if (cause.includes("No Active Trial")) {
            errorMessage = "";
            errorSubtype = "no_active_trial";
          } else if (
            cause.includes("Claude Credits Exhausted") ||
            cause.includes("Free Credits Exhausted")
          ) {
            // Free-tier user burned the one-time Claude grant — distinct card
            // so we can point them at free Gemini instead of a generic wall.
            errorMessage = "";
            errorSubtype = "free_credits_exhausted";
          } else if (cause.includes("Daily Gemini Limit Reached")) {
            errorMessage = "";
            errorSubtype = "gemini_daily_limit";
          } else if (
            cause.includes("Trial Expired") ||
            cause.includes("Trial Credits Exhausted") ||
            cause.includes("Trial Balance Exhausted")
          ) {
            errorMessage = "";
            errorSubtype = "trial_ended";
          } else if (cause.includes("Subscription Expired")) {
            errorMessage = "";
            errorSubtype = "subscription_expired";
          } else if (
            cause.includes("Usage Exceeded") ||
            cause.includes("Credits Exhausted") ||
            cause.includes("Balance Exhausted")
          ) {
            errorMessage = "";
            errorSubtype = "usage_exceeded";
          } else {
            errorMessage = "Access denied. Please check your subscription status.";
            errorSubtype = "access_denied";
          }
        } else if (
          error.surface === "stream" &&
          (
            cause.includes("already being worked on") ||
            cause.includes("already processing a request")
          )
        ) {
          errorMessage =
            "Please try again after the current execution finishes in this session.";
          errorSubtype = "session_busy";
        } else {
          errorMessage = error.message;
        }
      } else {
        errorMessage =
          "I apologize, but I encountered an error while processing your request. AI models can sometimes produce errors.";
        // Classify instead of logging subtype "(none)": if chunks were flowing
        // the connection died mid-stream; otherwise the stream never started.
        errorSubtype =
          liveStreamTelemetryRef.current.chunkOffsetsMs.length > 0
            ? "connection_lost"
            : "failed_to_start";
      }

      const hadStreamActivity = liveStreamTelemetryRef.current.startedAtMs !== null;
      const requestId = liveStreamTelemetryRef.current.requestId;

      // Metric reclassification (Aug 2026): decide the telemetry class BEFORE
      // emitting the terminal event so stream.error only counts genuinely
      // user-visible dead ends.
      // - Billing/usage walls show an intentional upgrade card → limit_hit.
      // - Drops routed into silent recovery (or a silent failed_to_start
      //   retry) are invisible to the user → interrupted.
      const isBillingWall =
        errorSubtype !== undefined && BILLING_WALL_SUBTYPES.has(errorSubtype);
      // Replay streams can't be recovered (useAutoResume short-circuits on
      // replayId). Routing replay errors into recovery would silently swallow
      // them and never show the error card to the user.
      const isRecoverableError =
        errorType === "unknown" && hadStreamActivity && requestId && !replayId;
      const willAutoRetry =
        errorSubtype === "failed_to_start" &&
        !replayId &&
        typeof navigator !== "undefined" &&
        navigator.onLine &&
        autoRetrySubmitCountRef.current < MAX_AUTO_RETRY_SUBMITS;

      finishLiveStream("error", {
        error_type: errorType,
        error_subtype: errorSubtype,
        error_message: errorMessage,
        // Raw error for diagnosis — error_message may be a user-facing canned string.
        error_raw:
          error instanceof Error
            ? `${error.name}: ${error.message}`.slice(0, 500)
            : String(error).slice(0, 500),
        reclassify: isBillingWall
          ? "limit_hit"
          : isRecoverableError || willAutoRetry
            ? "interrupted"
            : undefined,
      });
      refreshUsage();

      // Reliability fix (July 2026): failed_to_start = 0 chunks ever arrived,
      // so there is nothing to recover (backend has no request). If the device
      // is online, silently re-submit the last message before showing the
      // error card. Capped at MAX_AUTO_RETRY_SUBMITS per successful stream
      // (Aug 2026: raised 1 → 2; flaky mobile radios often need the second
      // try — 5 of 18 single retries still failed since launch).
      if (
        errorSubtype === "failed_to_start" &&
        !replayId &&
        typeof navigator !== "undefined" &&
        navigator.onLine
      ) {
        if (autoRetrySubmitCountRef.current < MAX_AUTO_RETRY_SUBMITS) {
          autoRetrySubmitCountRef.current += 1;
          telemetry.emit("stream.auto_retry_submit", {
            session_id: id,
            user_id: user?.uid,
            request_id: requestId ?? undefined,
            outcome: "attempt",
            attempt_number: autoRetrySubmitCountRef.current,
            error_message: errorMessage,
          });
          reload();
          return;
        }
        telemetry.emit("stream.auto_retry_submit", {
          session_id: id,
          user_id: user?.uid,
          request_id: requestId ?? undefined,
          outcome: "gave_up",
          attempt_number: autoRetrySubmitCountRef.current,
          error_message: errorMessage,
        });
      }

      if (isRecoverableError) {
        telemetry.emit("stream.error_routed_to_recovery", {
          session_id: id,
          user_id: user?.uid,
          request_id: requestId,
          recovery_version: "v1",
          error_message: errorMessage,
          chunks_received: liveStreamTelemetryRef.current.chunkOffsetsMs.length,
        });
        liveStreamAbortSourceRef.current = "drop_recovery";
        pendingDropRecoveryRequestIdRef.current = requestId;
        attemptedDropRecoveryRequestIdRef.current = requestId;
        return;
      }

      const newMessage = createErrorCardMessage({
        errorMessage,
        errorType,
        errorSubtype,
      });

      setMessages((currentMessages) => [...currentMessages, newMessage]);
    },
  });

  const [showReloadChat, setShowReloadChat] = useState(false);

  const handleRecoveryFailed = useCallback(() => {
    setShowReloadChat(true);
    telemetry.emit("stream.recovery_failed_shown", {
      session_id: id,
      user_id: user?.uid,
      recovery_version: "v1",
    });
  }, [id, user?.uid]);

  const handleReloadChat = useCallback(async () => {
    try {
      setShowReloadChat(false);
      const data = await sessionFetcher(id);
      if (data?.messages) {
        setMessages(convertToUIMessages(data.messages));
      }
      telemetry.emit("stream.manual_reload", {
        session_id: id,
        user_id: user?.uid,
        recovery_version: "v1",
        result: "ok",
      });
    } catch {
      setShowReloadChat(true);
      telemetry.emit("stream.manual_reload", {
        session_id: id,
        user_id: user?.uid,
        recovery_version: "v1",
        result: "error",
      });
    }
  }, [id, setMessages, user?.uid]);

  // ── SkipDbHydration ref (edit_alert handoff only) ─────────────────────
  // useAutoResume has several code paths that overwrite messages with a
  // freshly-fetched DB snapshot on mount (see use-auto-resume.ts
  // `safeHydrateFromDb`). On the edit_alert handoff, the user's optimistic
  // append() lands BEFORE the server has persisted the message, so a DB
  // hydration at that moment would wipe the user message ("flash then
  // vanish" bug). We flip this ref true RIGHT BEFORE the handoff append
  // fires and clear it as soon as:
  //   (a) status transitions to 'streaming' (server has our message + is
  //       responding → DB hydration is safe again)
  //   (b) status transitions to 'error' (stream never started, give up)
  //   (c) 30 seconds elapse as a hard-stop safety net so a stuck flag
  //       doesn't disable recovery indefinitely.
  //
  // CRITICAL: this ref is ONLY flipped true inside the initialUserQuery
  // branch (see below). For any chat session that didn't arrive via ?q=
  // handoff (regular /chat/{id}, replay viewer, plain /alerts/{id} visits
  // without ?q=), the ref remains false for the entire session →
  // useAutoResume's guard short-circuits → 100% unchanged behavior.
  const skipDbHydrationRef = useRef<boolean>(false);
  const skipDbHydrationSetAtRef = useRef<number | null>(null);

  const {
    isRecovering,
    isResumeStreaming,
    stopResuming,
    recoverNow,
  } = useAutoResume({
    autoResume,
    chatId: id,
    userId: user?.uid,
    replayId,
    messages,
    status,
    data,
    setData,
    setMessages,
    onRecoveryFailed: handleRecoveryFailed,
    // Optional ref: only ever flipped true during the edit_alert ?q=
    // handoff to prevent the recovery hook from wiping the user's
    // optimistic append before the server has persisted it. For every
    // non-handoff session this ref stays false, so useAutoResume's
    // guard short-circuits and behavior is identical to before.
    skipDbHydrationRef,
  });
  const pendingDropRecoveryRequestIdRef = useRef<string | null>(null);
  const attemptedDropRecoveryRequestIdRef = useRef<string | null>(null);
  // Reliability fix (July 2026): one-shot silent retry for failed_to_start
  // (stream died with 0 chunks while online — mobile Safari radio blips,
  // "Load failed" before the request reached the backend). 29/105 real errors
  // in the post-Jul-6 week were this class; recovery can't help because the
  // backend has no request to resume (/agent/request-status → not_found).
  // Reset ONLY in onFinish (successful stream) so a failing retry can never
  // loop: after MAX_AUTO_RETRY_SUBMITS attempts → gave_up → visible error
  // card, no further attempts. (Aug 2026: boolean one-shot → counter, cap 2.)
  const autoRetrySubmitCountRef = useRef(0);
  // Fix #1 (reliability v2): throttles the stream.drop_suppressed_hidden
  // telemetry so a backgrounded tab can't spam the event each watchdog tick.
  const lastHiddenSuppressMsRef = useRef(0);
  // Fix (reliability v3): backend request-status check on tab return / stale
  // streams. The v2 watchdog needs 30s of *continuous visible* inactivity to
  // declare a drop, but rapid tab-switching resets the activity clock on every
  // return — so a genuinely dead SSE connection is never declared dropped and
  // the spinner runs forever over a completed answer (June 2026 incident).
  // These refs throttle and dedupe the authoritative "is the backend actually
  // still running this request?" check.
  const statusCheckInFlightRef = useRef(false);
  const lastStatusCheckMsRef = useRef(0);

  const effectiveStatus =
    status === "submitted" || status === "streaming"
      ? status
      : isRecovering
        ? "streaming"
        : status;

  const resetLiveStreamTelemetry = useCallback(() => {
    liveStreamTelemetryRef.current = createLiveStreamTelemetryState();
    liveStreamAbortSourceRef.current = null;
  }, []);

  const emitLiveSummary = useCallback(
    (result: "complete" | "error" | "abort", extra: Record<string, unknown>) => {
      const run = liveStreamTelemetryRef.current;
      if (!run.startedAtMs || !run.startedAtIso) {
        return;
      }

      const nowMs = Date.now();
      telemetry.emit("stream.summary", {
        session_id: id,
        user_id: user?.uid,
        request_id: run.requestId ?? undefined,
        result,
        stream_phase: "live",
        started_at_client_iso: run.startedAtIso,
        ended_at_client_iso: new Date(nowMs).toISOString(),
        duration_ms: nowMs - run.startedAtMs,
        chunk_count: run.chunkOffsetsMs.length,
        heartbeat_count: run.heartbeatOffsetsMs.length,
        drop_count: run.dropOffsetsMs.length,
        chunk_offsets_ms: run.chunkOffsetsMs,
        heartbeat_offsets_ms: run.heartbeatOffsetsMs,
        drop_offsets_ms: run.dropOffsetsMs,
        visibility_events: run.visibilityEvents,
        network_events: run.networkEvents,
        max_chunk_gap_ms: run.maxGapMs,
        agent_type: run.meta.agentType,
        model_tier: run.meta.modelTier,
        deep_research: run.meta.deepResearch,
        has_attachments: run.meta.hasAttachments,
        message_len: run.meta.messageLen,
        is_replay: run.meta.isReplay,
        ...extra,
      });
    },
    [id, user?.uid],
  );

  const finishLiveStream = useCallback(
    (
      result: "complete" | "error" | "abort",
      extra: Record<string, unknown> = {},
    ) => {
      const run = liveStreamTelemetryRef.current;
      if (!run.startedAtMs || !run.startedAtIso) {
        return;
      }

      const nowMs = Date.now();
      const requestId =
        run.requestId ?? pendingStreamTelemetryRef.current.requestId ?? undefined;
      const endedAtIso = new Date(nowMs).toISOString();
      const terminalPayload = {
        session_id: id,
        user_id: user?.uid,
        request_id: requestId,
        duration_ms: nowMs - run.startedAtMs,
        chunks_received: run.chunkOffsetsMs.length,
        started_at_client_iso: run.startedAtIso,
        ended_at_client_iso: endedAtIso,
        max_chunk_gap_ms: run.maxGapMs,
        visibility_change_count: run.visibilityEvents.length,
        network_change_count: run.networkEvents.length,
        drop_count: run.dropOffsetsMs.length,
      };

      if (result === "complete") {
        telemetry.streamComplete(terminalPayload);
      } else if (result === "error") {
        const errorPayload = {
          ...terminalPayload,
          error_type:
            typeof extra.error_type === "string" ? extra.error_type : "unknown",
          error_subtype:
            typeof extra.error_subtype === "string"
              ? extra.error_subtype
              : undefined,
          error_message:
            typeof extra.error_message === "string"
              ? extra.error_message
              : "Unknown frontend stream error",
          error_raw:
            typeof extra.error_raw === "string" ? extra.error_raw : undefined,
        };
        // Metric reclassification (Aug 2026): billing walls and silently
        // recovered drops are NOT failures. Emitting them as stream.error made
        // the dashboard error rate look ~2x worse than reality (18% vs a true
        // ~0.2%). stream.error now means "user saw a dead end".
        const reclassify =
          typeof extra.reclassify === "string" ? extra.reclassify : null;
        if (reclassify === "limit_hit") {
          telemetry.emit("stream.limit_hit", errorPayload);
        } else if (reclassify === "interrupted") {
          telemetry.emit("stream.interrupted", errorPayload);
        } else {
          telemetry.streamError(errorPayload);
        }
      } else {
        telemetry.streamAbort({
          ...terminalPayload,
          abort_source:
            typeof extra.abort_source === "string"
              ? extra.abort_source
              : "status_transition",
        });
      }

      emitLiveSummary(result, extra);
      resetLiveStreamTelemetry();
    },
    [emitLiveSummary, id, resetLiveStreamTelemetry, user?.uid],
  );

  const recordLiveActivity = useCallback((kind: "chunk" | "heartbeat") => {
    const run = liveStreamTelemetryRef.current;
    if (!run.startedAtMs) {
      return;
    }

    const nowMs = Date.now();
    if (run.lastActivityAtMs !== null) {
      run.maxGapMs = Math.max(run.maxGapMs, nowMs - run.lastActivityAtMs);
    }
    run.lastActivityAtMs = nowMs;
    const offsetMs = nowMs - run.startedAtMs;
    if (kind === "chunk") {
      run.chunkOffsetsMs.push(offsetMs);
    } else {
      run.heartbeatOffsetsMs.push(offsetMs);
    }
  }, []);

  const effectiveStop = useCallback(() => {
    liveStreamAbortSourceRef.current =
      status === "submitted" || status === "streaming"
        ? "user_stop"
        : "recovery_stop";
    stopResuming();
    stop();
  }, [status, stop, stopResuming]);

  useEffect(() => {
    let cancelled = false;

    const loadAuthToken = async () => {
      if (!user) {
        authTokenRef.current = null;
        return;
      }

      try {
        const token = await user.getIdToken();
        if (!cancelled) authTokenRef.current = token;
      } catch {
        if (!cancelled) authTokenRef.current = null;
      }
    };

    void loadAuthToken();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Auto-register email notification when user closes/leaves tab while streaming
  const effectiveStatusRef = useRef(effectiveStatus);
  effectiveStatusRef.current = effectiveStatus;

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (
        effectiveStatusRef.current === "streaming" ||
        effectiveStatusRef.current === "submitted"
      ) {
        const run = liveStreamTelemetryRef.current;
        if (run.startedAtMs && run.startedAtIso) {
          const nowMs = Date.now();
          telemetry.emit("stream.beforeunload", {
            session_id: id,
            user_id: user?.uid,
            request_id: run.requestId ?? undefined,
            duration_ms: nowMs - run.startedAtMs,
            started_at_client_iso: run.startedAtIso,
            unload_at_client_iso: new Date(nowMs).toISOString(),
            chunk_count: run.chunkOffsetsMs.length,
            chunk_offsets_ms: run.chunkOffsetsMs,
            heartbeat_offsets_ms: run.heartbeatOffsetsMs,
            visibility_events: run.visibilityEvents,
            network_events: run.networkEvents,
            max_chunk_gap_ms: run.maxGapMs,
          });
        }

        const email = user?.email;
        const latestUserRequestId = [...messages]
          .reverse()
          .find((message) => message.role === "user")?.id;
        const authToken = authTokenRef.current;

        if (email && latestUserRequestId && authToken) {
          fetch(
            `${API_BASE_URL}/agent/notify-on-complete`,
            {
              method: "POST",
              keepalive: true,
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authToken}`,
              },
              body: JSON.stringify({
                session_id: id,
                user_email: email,
                target_request_id: latestUserRequestId,
              }),
            }
          );
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [id, messages, user?.email]);

  // Show below-fold content
  useEffect(() => {
    if (user || messages.length !== 0) {
      setShowBelowFold(true);
      return;
    }

    let frame1 = 0;
    let frame2 = 0;
    frame1 = window.requestAnimationFrame(() => {
      frame2 = window.requestAnimationFrame(() => setShowBelowFold(true));
    });

    return () => {
      window.cancelAnimationFrame(frame1);
      window.cancelAnimationFrame(frame2);
    };
  }, [messages.length, user]);

  // Handle initial query from URL params
  const hasAppendedQueryRef = useRef(false);
  // Note: skipDbHydrationRef + skipDbHydrationSetAtRef are declared ABOVE
  // the useAutoResume() call (around line ~580) because useAutoResume
  // needs the ref passed in, and JS TDZ means we can't reference them
  // here before that declaration. Don't redeclare here.

  useEffect(() => {
    // Log every time this effect fires so we can tell in the handoff log
    // whether (a) the effect is running at all, (b) initialUserQuery arrived,
    // (c) the ref-guard is blocking re-runs on remount. This is critical —
    // if <Chat> remounts, hasAppendedQueryRef resets to false but the session
    // may already have the append'd message, skewing the gate upstream.
    handoffLog("chat:effect:fired", {
      t: tSinceNav(),
      hasQuery: Boolean(initialUserQuery),
      queryLen: initialUserQuery?.length ?? 0,
      alreadyAppended: hasAppendedQueryRef.current,
      sessionId: id,
      isAlertsRoute,
      hasSubAgents: Boolean(hasSubAgents),
      replayId: replayId || null,
    });

    if (initialUserQuery && !hasAppendedQueryRef.current) {
      // Anti-replay guard: if the user refreshes /alerts/{id}?q=... with the
      // same ?q= still in the URL, we'd otherwise re-submit. Keyed by the
      // sessionId + a cheap hash of the query so (a) the same query in a
      // DIFFERENT alert session still submits, (b) a DIFFERENT query in the
      // same session still submits, (c) the exact same (session, query) pair
      // only submits once. sessionStorage scope = one browser tab, which is
      // correct: opening the same ?q= in a new tab is an explicit user action
      // and we want it to submit there.
      const storageKey = `edit_alert_submitted:${id}:${cheapHash(initialUserQuery)}`;
      let alreadySubmittedInThisTab = false;
      try {
        alreadySubmittedInThisTab =
          typeof window !== "undefined" &&
          window.sessionStorage.getItem(storageKey) === "1";
      } catch {
        // sessionStorage may be blocked (Safari private mode, quota). Fall
        // through as not-submitted — the worst case is a double-submit on
        // refresh, which is rare and the user can see + cancel.
      }

      if (alreadySubmittedInThisTab) {
        handoffLog("chat:append:skipped", {
          t: tSinceNav(),
          reason: "already_submitted_this_tab",
          storageKey,
          sessionId: id,
        });
        hasAppendedQueryRef.current = true;
        // Still strip the URL so a further refresh doesn't keep spinning on
        // the same ?q=. Same logic as the success path below.
        if (!replayId) {
          try {
            if (isAlertsRoute) {
              window.history.replaceState({}, "", window.location.pathname);
            } else {
              window.history.replaceState({}, "", `/chat/${id}`);
            }
          } catch {
            // noop
          }
        }
        return;
      }

      hasAppendedQueryRef.current = true;
      const msgId = generateUUID();
      if (hasSubAgents && replayId) {
        subAgentMessageIdsRef.current.add(msgId);
      }

      handoffLog("chat:append:calling", {
        t: tSinceNav(),
        msgId,
        queryLen: initialUserQuery.length,
        queryPreview: initialUserQuery.slice(0, 80),
        sessionId: id,
        storageKey,
      });

      // Block useAutoResume from clobbering the optimistic append while the
      // user's message is flying up and the server hasn't persisted it yet.
      // Cleared by the effect below once status transitions (or 30s passes).
      skipDbHydrationRef.current = true;
      skipDbHydrationSetAtRef.current = Date.now();
      handoffLog("chat:skip_db_hydration:set", {
        t: tSinceNav(),
        sessionId: id,
        msgId,
      });

      // Run-now auto-submit: arm the skill id for THIS message so the backend
      // injects the engineered instruction and the artifact card renders.
      if (initialActiveSkill?.id) {
        activeSkillIdRef.current = [initialActiveSkill.id];
        // eslint-disable-next-line no-console
        console.log("[SKILL-LAUNCH] auto-submit arming skill_id", {
          skillId: initialActiveSkill.id,
          msgId,
          sessionId: id,
        });
      }
      try {
        append({
          role: "user",
          content: initialUserQuery,
          id: msgId,
        });
        // Mark submitted BEFORE the URL strip so even if the subsequent
        // replaceState throws we don't double-submit on a re-render.
        try {
          window.sessionStorage.setItem(storageKey, "1");
        } catch {
          // noop
        }
        handoffLog("chat:append:called", { t: tSinceNav(), msgId, threw: false });
      } catch (err) {
        handoffLog("chat:append:called", {
          t: tSinceNav(),
          msgId,
          threw: true,
          error: String(err),
        });
        throw err;
      }

      if (!replayId) {
        // When we're on /alerts/{alertId}, keep the pathname and just drop
        // the ?q= param. Using `/chat/${id}` (sessionId) here would kick the
        // user off the alerts page onto /chat/, which is wrong for the
        // edit_alert handoff flow. We strip the query via pathname-only
        // replaceState so a refresh doesn't re-auto-submit the same query.
        const beforePath = window.location.pathname;
        const beforeSearch = window.location.search;
        handoffLog("chat:url:stripping", {
          t: tSinceNav(),
          beforePath,
          beforeSearch,
          isAlertsRoute,
        });
        if (isAlertsRoute) {
          window.history.replaceState({}, "", window.location.pathname);
        } else {
          window.history.replaceState({}, "", `/chat/${id}`);
        }
        handoffLog("chat:url:stripped", {
          t: tSinceNav(),
          afterPath: window.location.pathname,
          afterSearch: window.location.search,
        });
      }
    }
  }, [initialUserQuery, id, append, isAlertsRoute, initialActiveSkill]);

  // ── Handoff observability: messages array + status transitions ────────
  // Added to debug the "chat panel opens but user message + stream don't
  // show up for a few seconds" gap on the edit_alert handoff. We want to
  // pinpoint the exact moment between (a) append() being called and
  // (b) the user's message actually landing in the rendered messages
  // array, and (c) the AI SDK status moving from "ready" → "submitted" →
  // "streaming". Only logs when we're on the alerts route to avoid
  // drowning normal chat telemetry.
  const handoffMsgCountRef = useRef<number>(-1);
  const handoffStatusRef = useRef<string>("");
  const handoffChatFirstSeenRef = useRef<boolean>(false);

  useEffect(() => {
    if (!isAlertsRoute) return;

    if (!handoffChatFirstSeenRef.current) {
      handoffChatFirstSeenRef.current = true;
      handoffLog("chat:mounted", {
        t: tSinceNav(),
        sessionId: id,
        initialMessagesLen: initialMessages.length,
        messagesLen: messages.length,
        status,
        hasInitialUserQuery: Boolean(initialUserQuery),
      });
    }

    const prevCount = handoffMsgCountRef.current;
    if (prevCount !== messages.length) {
      handoffMsgCountRef.current = messages.length;
      const last = messages[messages.length - 1];
      handoffLog("chat:messages:changed", {
        t: tSinceNav(),
        sessionId: id,
        prevCount,
        newCount: messages.length,
        lastMessageRole: last?.role ?? null,
        lastMessageId: last?.id ?? null,
        lastContentLen:
          typeof last?.content === "string" ? last.content.length : 0,
        lastContentPreview:
          typeof last?.content === "string" ? last.content.slice(0, 60) : null,
        status,
      });
    }

    const prevStatus = handoffStatusRef.current;
    if (prevStatus !== status) {
      handoffStatusRef.current = status;
      handoffLog("chat:status:changed", {
        t: tSinceNav(),
        sessionId: id,
        prevStatus: prevStatus || "(init)",
        newStatus: status,
        messagesLen: messages.length,
      });
    }
  }, [messages, status, isAlertsRoute, id, initialMessages.length, initialUserQuery]);

  // ── Autoclear skipDbHydrationRef on status transitions + 30s timeout ──
  // This runs on EVERY session (not just alerts/handoff) but only takes
  // action if the ref was actually set true — which only happens inside
  // the initialUserQuery handoff branch above. Non-handoff sessions never
  // set the ref true, so this effect is a no-op for them.
  useEffect(() => {
    if (!skipDbHydrationRef.current) return;

    // Clear on streaming (server has our message + is responding) or error
    // or ready-after-submitted (request completed).
    if (status === "streaming" || status === "error") {
      const reason = status === "streaming"
        ? "status_streaming"
        : "status_error";
      skipDbHydrationRef.current = false;
      skipDbHydrationSetAtRef.current = null;
      handoffLog("chat:skip_db_hydration:cleared", {
        t: tSinceNav(),
        sessionId: id,
        reason,
      });
      return;
    }

    // Hard-stop safety: 30s after being set, force-clear even if status
    // never transitioned. Prevents a stuck flag from disabling recovery
    // forever on a broken session.
    const setAt = skipDbHydrationSetAtRef.current;
    if (setAt !== null) {
      const elapsed = Date.now() - setAt;
      const remainingMs = 30_000 - elapsed;
      if (remainingMs <= 0) {
        skipDbHydrationRef.current = false;
        skipDbHydrationSetAtRef.current = null;
        handoffLog("chat:skip_db_hydration:cleared", {
          t: tSinceNav(),
          sessionId: id,
          reason: "timeout_30s",
          elapsed_ms: elapsed,
        });
        return;
      }
      // Schedule a re-check at the timeout boundary (so we clear even if
      // no further status change fires to re-run this effect).
      const timer = setTimeout(() => {
        if (!skipDbHydrationRef.current) return;
        skipDbHydrationRef.current = false;
        skipDbHydrationSetAtRef.current = null;
        handoffLog("chat:skip_db_hydration:cleared", {
          t: tSinceNav(),
          sessionId: id,
          reason: "timeout_30s_scheduled",
        });
      }, remainingMs);
      return () => clearTimeout(timer);
    }
  }, [status, id]);

  // Handle prefill from URL params
  const hasPrefilled = useRef(false);

  useEffect(() => {
    if (initialPrefill && !hasPrefilled.current) {
      hasPrefilled.current = true;
      setInput(initialPrefill);
      // The composer textarea is driven by MultimodalInput's local `draft`
      // state, not useChat's `input`, so route the prefill through the
      // same event the alerts-onboarding CTA uses. The listener also
      // mirrors to localStorage and focuses the textarea.
      window.dispatchEvent(
        new CustomEvent("xynth:prefill-input", {
          detail: { text: initialPrefill },
        })
      );
      window.history.replaceState({}, "", `/`);
    }
  }, [initialPrefill, setInput]);

  // Cross-component input prefill via custom DOM event. Used by the
  // alerts onboarding modal's "Build your first alert" CTA to drop a
  // starter prompt into the chat textarea without a full page reload
  // or URL change. Any caller can fire:
  //   window.dispatchEvent(new CustomEvent("xynth:prefill-input",
  //     { detail: { text: "..." } }));
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ text?: string }>).detail;
      prefillLog("chat:received", {
        sessionId: id,
        textLen: detail?.text?.length ?? 0,
        textPreview: detail?.text?.slice(0, 60) ?? null,
      });
      if (detail?.text) {
        try {
          setInput(detail.text);
          prefillLog("chat:setInput:ok", { sessionId: id });
        } catch (err) {
          prefillLog("chat:setInput:error", {
            sessionId: id,
            error: String(err),
          });
        }
      }
    };
    prefillLog("chat:listener:attached", { sessionId: id });
    window.addEventListener("xynth:prefill-input", handler);
    return () => {
      prefillLog("chat:listener:detached", { sessionId: id });
      window.removeEventListener("xynth:prefill-input", handler);
    };
  }, [setInput, id]);

  // Consultation events (needed for artifact/notebook refresh)
  const { pushEvent: pushConsultationEvent, reset: resetConsultation } = useConsultationEvents();
  const dataIndexRef = useRef(0);

  // ── nextUserMessage: derived synchronously during render ──────────────
  // Critical for replays. The SSE stream emits `2:[{type:next_user_message,
  // content}]` immediately before the terminal `d:` event that flips AI
  // SDK status from streaming → ready. Both updates batch into one
  // re-render. If we read this value via a useState set inside a
  // useEffect, ReplayControls (the child) sees stale `null` on the first
  // post-stream render — its own isComplete effect (which fires BEFORE
  // parent effects per React's child-first ordering) reads the stale
  // prop, decides the replay finished, and unmounts ReplayControls
  // before chat.tsx ever updates the state. Result: Continue button
  // never appears, branch falls through to the "Back to /" CTA.
  // Deriving via useMemo guarantees the correct value is on the prop
  // at render time — before any effect can fire.
  const [nextUserMessageConsumedAt, setNextUserMessageConsumedAt] = useState(0);
  const nextUserMessage = useMemo<string | null>(() => {
    if (!data || data.length === 0) {
      if (replayId) replayLog("useMemo:empty", { dataLen: data?.length ?? 0, status });
      return null;
    }
    for (let i = data.length - 1; i >= nextUserMessageConsumedAt; i--) {
      const item = data[i] as any;
      if (item?.type === "next_user_message" && item?.content) {
        if (replayId) replayLog("useMemo:found", {
          idx: i,
          content: item.content,
          dataLen: data.length,
          consumedAt: nextUserMessageConsumedAt,
          status,
        });
        return item.content;
      }
    }
    if (replayId) replayLog("useMemo:missing", {
      dataLen: data.length,
      consumedAt: nextUserMessageConsumedAt,
      status,
      types: data.slice(nextUserMessageConsumedAt).map((d: any) => d?.type ?? typeof d),
    });
    return null;
  }, [data, nextUserMessageConsumedAt, status, replayId]);

  // ── publishGate: pause-to-publish handshake during replay ─────────────
  // Mirrors `nextUserMessage` above. The backend's replay stream emits
  // `2:[{type:"publish_gate", alert_name, schedule_description, ...}]`
  // immediately before it would have streamed the `upload_alert` tool
  // result, then sets `state.paused=True` server-side. The viewer sees
  // a Publish button rendered in the bottom replay bar and, on click,
  // we POST `/replay/control` with `action=resume` to release the
  // backend busy-loop. This mirrors the live UX (a real user explicitly
  // clicks Publish before the alert goes live) and is the most
  // satisfying interaction beat in any screen-recorded demo.
  //
  // Why a separate "consumed" cursor: clicking Publish must clear the
  // gate immediately so it doesn't re-render on the next render pass
  // when `data` still contains the (now-stale) publish_gate item.
  // We can't mutate `data` (it's owned by useChat) so we advance a
  // cursor past it.
  const [publishGateConsumedAt, setPublishGateConsumedAt] = useState(0);
  const publishGate = useMemo<{
    alertName: string | null;
    scheduleDescription: string | null;
    toolCallId: string | null;
  } | null>(() => {
    // Perf guard: in a regular (non-replay) chat this memo would re-run
    // on every SSE chunk (data is a new array each time) and scan the
    // whole data array looking for an event type that will never exist.
    // Short-circuit here so normal chats pay nothing.
    if (!replayId) return null;
    if (!data || data.length === 0) return null;
    for (let i = data.length - 1; i >= publishGateConsumedAt; i--) {
      const item = data[i] as any;
      if (item?.type === "publish_gate") {
        return {
          alertName: typeof item.alert_name === "string" ? item.alert_name : null,
          scheduleDescription:
            typeof item.schedule_description === "string"
              ? item.schedule_description
              : null,
          toolCallId:
            typeof item.tool_call_id === "string" ? item.tool_call_id : null,
        };
      }
    }
    return null;
  }, [replayId, data, publishGateConsumedAt]);

  // Listen for the in-message Publish card's confirmation event so the
  // bottom-bar Publish button clears in lockstep. ONLY subscribes when
  // replayId is set — normal chats pay zero cost (effect body is an
  // empty early-return). Dependencies are kept minimal (replayId + id)
  // so we don't re-attach the listener on every data chunk; the handler
  // reads the current data length from a render-sync ref instead.
  const dataLenRef = useRef(0);
  dataLenRef.current = data?.length ?? 0;
  useEffect(() => {
    if (!replayId) return;
    const handler = (e: Event) => {
      const ev = e as CustomEvent<{ sessionId?: string }>;
      if (ev.detail?.sessionId && ev.detail.sessionId !== id) return;
      // Advance cursor to the live data length so currently-buffered
      // publish_gate items are marked consumed. A new gate in a later
      // turn appends past this cursor and the memo picks it up again
      // (using MAX_SAFE_INTEGER here would break multi-turn replays).
      setPublishGateConsumedAt(dataLenRef.current);
    };
    window.addEventListener("xynth:replay-publish-confirmed", handler);
    return () => {
      window.removeEventListener("xynth:replay-publish-confirmed", handler);
    };
  }, [replayId, id]);

  // ── buddyBeat: onboarding-tour gate during replay ─────────────────────
  // Mirrors the publishGate pattern above. Backend emits
  // `2:[{type:"buddy_beat", beat_id, trigger, copy, anchor, action}]`
  // at scripted trigger points (before_first_message, after_add_cell:N),
  // then busy-waits until the frontend POSTs `/replay/buddy-gate/release`.
  // See app/api/routes/replay.py::_replay_buddy.
  //
  // Why a consumed cursor (not just "latest event"):
  //   - A single replay fires many beats; clicking Continue must clear
  //     the current one even though `data` still contains its item.
  //   - We can't mutate `data` (useChat owns it), so we advance a cursor
  //     past beat items we've already handled.
  //   - Scanning from `buddyConsumedAt` forward finds the NEXT pending
  //     beat deterministically — even if multiple arrived in the same
  //     chunk (rare but possible around step-finish boundaries).
  const [buddyConsumedAt, setBuddyConsumedAt] = useState(0);
  type BuddyAnchorShape =
    | { kind: "cell_index"; value: number }
    | { kind: "cell_id"; value: string }
    | { kind: "css"; value: string }
    | { kind: "latest_role"; value: string; inner?: string }
    | null;
  const buddyBeat = useMemo<{
    eventIndex: number;
    beatId: string;
    trigger: { type: string; index?: number };
    copy: { title?: string; body?: string; cta?: string };
    anchor: BuddyAnchorShape;
    actionTarget?: BuddyAnchorShape;
    spotlight: BuddyAnchorShape;
    bubbleAnchor?: BuddyAnchorShape;
    bubbleOffset?: { x?: number; y?: number };
    action: { kind?: string; demo?: string } | null;
    spotlightRadius?: number;
    clickHintPlacement?: "auto" | "above";
    highlightOnly?: boolean;
    noAnchor?: boolean;
    bubblePlacement?: "center" | "bottomRight" | "topCenter" | "anchor" | "anchorAbove" | "anchorBelow" | "anchorTopRight";
    scrollOffsetY?: number;
  } | null>(() => {
    if (!replayId) return null;
    if (!data || data.length === 0) return null;
    // Forward scan (FIFO) so beats are consumed in arrival order. Breaks
    // at the first un-consumed beat — its release bumps the cursor past
    // it and the next render surfaces the next beat if any.
    for (let i = buddyConsumedAt; i < data.length; i++) {
      const item = data[i] as any;
      if (item?.type !== "buddy_beat") continue;
      const beatId = typeof item.beat_id === "string" ? item.beat_id : null;
      if (!beatId) continue;
      return {
        eventIndex: i,
        beatId,
        trigger: item.trigger ?? { type: "" },
        copy: item.copy ?? {},
        anchor: item.anchor ?? null,
        actionTarget: item.actionTarget ?? undefined,
        spotlight: item.spotlight ?? null,
        bubbleAnchor: item.bubbleAnchor ?? undefined,
        bubbleOffset: item.bubbleOffset,
        action: item.action ?? null,
        spotlightRadius: item.spotlightRadius,
        clickHintPlacement: item.clickHintPlacement,
        highlightOnly: item.highlightOnly,
        noAnchor: item.noAnchor,
        bubblePlacement: item.bubblePlacement,
        scrollOffsetY: item.scrollOffsetY,
      };
    }
    return null;
  }, [replayId, data, buddyConsumedAt]);

  // Per-beat dwell tracking for telemetry. Key = beat_id, value = ms
  // timestamp when the beat first became visible to the user. We
  // measure dwell as (release_click_ts − reached_ts) so we can find
  // which beats users stare at (= copy is confusing or content is
  // slow to load) versus which they speed through (= filler beats
  // that could be cut). Cleared on release so re-entries (rare) start
  // a fresh clock.
  const buddyBeatStartTsRef = useRef<Record<string, number>>({});
  const buddyBeatReachedEmittedRef = useRef<Set<string>>(new Set());

  // Fire `replay.beat_reached` exactly once per distinct beat_id seen.
  // Mirrors how `publishGate` is detected — when `buddyBeat` flips from
  // null/different to a new beatId we record the reach timestamp and
  // emit the telemetry event. The released event below pairs back to
  // this to compute `dwell_ms`.
  useEffect(() => {
    if (!replayId) return;
    const beatId = buddyBeat?.beatId;
    if (!beatId) return;
    if (buddyBeatReachedEmittedRef.current.has(beatId)) return;
    buddyBeatReachedEmittedRef.current.add(beatId);
    buddyBeatStartTsRef.current[beatId] = Date.now();
    telemetry.emit("replay.beat_reached", {
      session_id: id,
      curated_id: replayId,
      beat_id: beatId,
      anchor: buddyBeat?.anchor,
      action: buddyBeat?.action,
    });
  }, [replayId, id, buddyBeat?.beatId, buddyBeat?.anchor, buddyBeat?.action]);

  // Release a buddy beat: advance local cursor first (clears overlay
  // immediately even if the network request is slow), then POST the
  // backend release. Idempotent server-side so retries are safe.
  const releaseBuddyBeat = useCallback(
    (beatId: string) => {
      setBuddyConsumedAt((current) => {
        if (buddyBeat?.beatId === beatId) {
          return Math.max(current, buddyBeat.eventIndex + 1);
        }
        return Math.max(current, dataLenRef.current);
      });
      // Per-beat telemetry. dwell_ms is the wall-time the buddy beat
      // sat on screen before this Next click. Combined with the
      // matching `replay.beat_reached` event we can derive funnel
      // drop-off (count of reached vs released per beat_id) and copy
      // quality (p50/p90 dwell per beat_id) from a single Mongo
      // aggregation.
      const reachedAt = buddyBeatStartTsRef.current[beatId];
      const dwellMs = reachedAt ? Date.now() - reachedAt : null;
      if (replayId) {
        telemetry.emit("replay.beat_released", {
          session_id: id,
          curated_id: replayId,
          beat_id: beatId,
          dwell_ms: dwellMs,
          anchor: buddyBeat?.beatId === beatId ? buddyBeat?.anchor : undefined,
          action: buddyBeat?.beatId === beatId ? buddyBeat?.action : undefined,
        });
      }
      delete buddyBeatStartTsRef.current[beatId];
      fetch(`${API_BASE_URL}/replay/buddy-gate/release`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: id, beat_id: beatId }),
        keepalive: true,
      }).catch((err) => {
        console.warn("[buddy] release POST failed", beatId, err);
      });
    },
    [buddyBeat?.beatId, buddyBeat?.eventIndex, buddyBeat?.anchor, buddyBeat?.action, id, replayId]
  );

  // Exit the tour and route to homepage. Emits a `replay.exited`
  // telemetry event with the last beat the user saw so we can
  // distinguish "completed the tour" (release of the final beat)
  // from "bailed early" (exit at beat N < final).
  const handleBuddyExit = useCallback(() => {
    if (replayId) {
      telemetry.emit("replay.exited", {
        session_id: id,
        curated_id: replayId,
        last_beat_id: buddyBeat?.beatId ?? null,
      });
    }
    router.push("/");
  }, [router, replayId, id, buddyBeat?.beatId]);

  useEffect(() => {
    if (!data || data.length === 0) {
      dataIndexRef.current = 0;
      return;
    }

    let shouldRefreshNotebook = false;

    for (let i = dataIndexRef.current; i < data.length; i++) {
      const item = data[i] as any;
      if (
        (status === "submitted" || status === "streaming") &&
        liveStreamTelemetryRef.current.startedAtMs
      ) {
        if (
          item?.type === "redis_chunk_id" &&
          typeof item?.content === "string" &&
          item.content !== liveStreamTelemetryRef.current.lastChunkId
        ) {
          liveStreamTelemetryRef.current.lastChunkId = item.content;
          if (typeof item?.request_id === "string") {
            liveStreamTelemetryRef.current.requestId = item.request_id;
          }
          recordLiveActivity("chunk");
        } else if (
          item?.type === "heartbeat" &&
          item?.source === "stream_route_keepalive"
        ) {
          recordLiveActivity("heartbeat");
        }
      }
      if (item?.consultation === true) {
        pushConsultationEvent(item);
        shouldRefreshNotebook = true;
      }
      if (item?.type === "next_user_message" && item?.content) {
        // Notebook refresh is the only remaining side effect — the
        // value itself is computed synchronously above.
        shouldRefreshNotebook = true;
      }
    }
    dataIndexRef.current = data.length;

    if (shouldRefreshNotebook) {
      mutate(`${API_BASE_URL}/notebook/by-session/${id}`);
    }
  }, [data, id, mutate, pushConsultationEvent, recordLiveActivity, status]);

  // Reset consultation state when a new stream starts
  useEffect(() => {
    if (status === 'submitted' || isResumeStreaming) {
      resetConsultation();
    }
  }, [isResumeStreaming, resetConsultation, status]);

  // Replay debugging: log status transitions + data-array growth.
  const replayStatusRef = useRef<string>(status);
  const replayDataLenRef = useRef<number>(data?.length ?? 0);
  // Lifecycle telemetry refs — emit `replay.started` exactly once on the
  // first stream activity for this session, and `replay.completed` when
  // status transitions to a terminal `ready` state. Tracked as refs so
  // we don't re-emit on every chunk and don't double-emit on remounts.
  const replayStartedEmittedRef = useRef(false);
  const replayCompletedEmittedRef = useRef(false);
  const replayStartTsRef = useRef<number>(0);
  useEffect(() => {
    if (!replayId) return;
    const prevStatus = replayStatusRef.current;
    const prevLen = replayDataLenRef.current;
    const currLen = data?.length ?? 0;
    if (prevStatus !== status) {
      replayLog("status:change", { from: prevStatus, to: status, dataLen: currLen });
      // Fire `replay.started` on first transition INTO a streaming
      // state. We deliberately don't fire on mount — that would log
      // every refresh of the page even when the replay never actually
      // streams.
      if (
        !replayStartedEmittedRef.current &&
        (status === "submitted" || status === "streaming") &&
        prevStatus !== "submitted" &&
        prevStatus !== "streaming"
      ) {
        replayStartedEmittedRef.current = true;
        replayStartTsRef.current = Date.now();
        telemetry.emit("replay.started", {
          session_id: id,
          curated_id: replayId,
          start_at_beat: replayStartAtBeat ?? null,
        });
      }
      // Fire `replay.completed` when the stream lands in `ready`
      // after at least one streaming pass — `ready` is the terminal
      // state from useChat. Pairs with `replay.started` for activation
      // funnel queries.
      if (
        !replayCompletedEmittedRef.current &&
        status === "ready" &&
        (prevStatus === "streaming" || prevStatus === "submitted")
      ) {
        replayCompletedEmittedRef.current = true;
        const startedAt = replayStartTsRef.current;
        telemetry.emit("replay.completed", {
          session_id: id,
          curated_id: replayId,
          total_duration_ms: startedAt ? Date.now() - startedAt : null,
          chunks_seen: currLen,
        });
      }
      replayStatusRef.current = status;
    }
    if (currLen !== prevLen) {
      const newItems = data?.slice(prevLen) ?? [];
      replayLog("data:grew", {
        from: prevLen,
        to: currLen,
        newTypes: newItems.map((d: any) => d?.type ?? typeof d),
        status,
      });
      replayDataLenRef.current = currLen;
    }
  }, [status, data, replayId, id, replayStartAtBeat]);

  // Fire `replay.publish_gate_shown` exactly once per publish-gate
  // emission. Pairs with the `replay.publish_gate_resumed` event we
  // fire inside `onReplayPublishConfirm` below — together they let us
  // measure (a) how many users reach the publish moment at all (the
  // emotional climax of the tour) and (b) how long they sit on the
  // Publish button before clicking. Big copy/UX signal.
  const publishGateShownEmittedRef = useRef(false);
  useEffect(() => {
    if (!replayId) return;
    if (!publishGate) {
      // Reset when gate clears so a re-emission (in theory possible if
      // the user re-enters the tour) can fire again.
      publishGateShownEmittedRef.current = false;
      return;
    }
    if (publishGateShownEmittedRef.current) return;
    publishGateShownEmittedRef.current = true;
    telemetry.emit("replay.publish_gate_shown", {
      session_id: id,
      curated_id: replayId,
      alert_name: publishGate.alertName,
      schedule_description: publishGate.scheduleDescription,
    });
  }, [replayId, id, publishGate]);

  const { setArtifact: setArtifactForMode } = useArtifact();
  const processedSwitchModeCallsRef = useRef<Set<string>>(new Set());
  const processedUploadAlertCallsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");
    if (!lastAssistant?.parts) return;
    for (const part of lastAssistant.parts as any[]) {
      if (
        part?.type === "tool-invocation" &&
        part?.toolInvocation?.toolName === "switch_mode"
      ) {
        const state = part?.toolInvocation?.state;
        const result = part?.toolInvocation?.result;
        const args = part?.toolInvocation?.args;
        const toolCallId = part?.toolInvocation?.toolCallId;
        if (state === "result" && toolCallId) {
          if (processedSwitchModeCallsRef.current.has(toolCallId)) continue;
          processedSwitchModeCallsRef.current.add(toolCallId);
        }
        debugLog("switch_mode-observed", {
          session_id: id,
          state,
          args_mode: args?.mode,
          result_view_mode: result?.view_mode,
          result_status: result?.status,
          result_keys: result ? Object.keys(result) : [],
          current_view_mode: viewMode,
          is_mobile: isMobile,
        });
        if (state !== "result") continue;
        const newMode = result?.view_mode;
        if (!newMode) {
          debugLog("switch_mode-no-view-mode-in-result", {
            session_id: id,
            result,
          });
          continue;
        }
        if (newMode !== viewMode) {
          // Alert/signal-linked sessions are PERMANENTLY locked to the mode
          // the page opened in. If the agent (or a stale switch_mode call)
          // tries to flip away, we ignore it — there's no research mode on
          // /alerts/[id], and a signal must stay a signal (green) not flip
          // to the icy alert theme.
          if (
            (initialViewMode === "alert" || initialViewMode === "signal") &&
            newMode !== initialViewMode
          ) {
            console.warn(
              "[chat] switch_mode ignored — alert/signal session is locked",
              { session_id: id, locked_to: initialViewMode, attempted: newMode },
            );
          } else {
            setViewMode(newMode);
            mutate(`session-${id}-${user?.uid || "anonymous"}`);
          }
        }
        // Update alert_start_cell_index from switch_mode result — lets the canvas
        // filter to only alert-scoped cells without waiting for a session refetch.
        const newStartIdx = result?.alert_start_cell_index;
        // "signal" is a build mode just like "alert" — same canvas, same
        // cell scoping. Keep BOTH in sync everywhere or the canvas
        // silently never opens (the bug that hit signal mode on launch).
        const isBuildMode = newMode === "alert" || newMode === "signal";
        if (isBuildMode && typeof newStartIdx === "number") {
          setAlertStartCellIndex(newStartIdx);
        } else if (newMode === "research") {
          setAlertStartCellIndex(null);
        }
        if (isBuildMode) {
          debugLog("switch_mode-opening-artifact", {
            session_id: id,
            new_mode: newMode,
            is_mobile: isMobile,
          });
          telemetry.emit("alert_canvas.layout_state", {
            session_id: id,
            user_id: user?.uid,
            source: "switch_mode_alert",
            is_mobile: isMobile,
            is_visible: true,
            is_chat_minimized: isMobile,
            is_artifact_minimized: false,
          });
          setArtifactForMode((prev) => ({
            ...prev,
            isVisible: true,
            documentId: id,
            // Desktop alert-building should always land in split view.
            // Mobile alert-building should land in canvas-full mode.
            isChatMinimized: isMobile,
            isArtifactMinimized: false,
          }));
        }
      }
      // upload_alert success → backend auto-flips view_mode to research + clears
      // alert_start_cell_index. Mirror that on the frontend immediately so the
      // canvas disappears cleanly without waiting for a session refetch.
      if (
        part?.type === "tool-invocation" &&
        part?.toolInvocation?.toolName === "upload_alert"
      ) {
        const state = part?.toolInvocation?.state;
        const result = part?.toolInvocation?.result;
        const toolCallId = part?.toolInvocation?.toolCallId;
        if (state === "result" && toolCallId) {
          if (processedUploadAlertCallsRef.current.has(toolCallId)) continue;
          processedUploadAlertCallsRef.current.add(toolCallId);
        }
        if (state === "result" && result?.status === "success") {
          // Alert-linked sessions (/alerts/[id]) must STAY in alert mode
          // after upload — the session's whole purpose is alert editing and
          // there is no research surface to return to. Only flip back to
          // research if this was a regular /chat/[id] upload.
          if (initialViewMode === "alert") {
            console.info(
              "[chat] upload_alert success in alert-locked session — staying in alert mode",
              { session_id: id },
            );
          } else {
            if (viewMode === "alert" || viewMode === "signal") setViewMode("research");
            setAlertStartCellIndex(null);
            // The alert is published — close the Alert Canvas artifact panel.
            // switch_mode("alert") opened it (isVisible:true) but nothing here
            // hid it on the flip back to research, so the empty canvas
            // ("Waiting for the agent to lay out your alert") stayed pinned
            // open after publishing — the reported "still in alert mode" bug.
            setArtifactForMode((prev) => ({
              ...prev,
              isVisible: false,
              isChatMinimized: false,
              isArtifactMinimized: false,
            }));
          }
          mutate(`session-${id}-${user?.uid || "anonymous"}`);
        }
      }
    }
  }, [messages, viewMode, mutate, id, user?.uid, isMobile, setArtifactForMode]);

  // Votes
  const { data: votes } = useSWR<Array<Vote>>(
    messages.length >= 2 ? `votes-${id}` : null,
    () => apiClient.getVotes(id)
  );

  const [attachments, setAttachments] = useState<Array<Attachment>>([]);
  // Guided-tour replays default to 2x — the buddy script narrates each
  // cell as it lands, and a 1x stream pace is too slow for an onboarding
  // walkthrough. The user can still nudge the speed via the floating
  // speed control. Non-tour replays (one-off curated sessions w/o a
  // buddy script) keep the natural 1x pace.
  const [replaySpeed, setReplaySpeed] = useState(
    replayCleanMode
      ? CLEAN_REPLAY_SPEED
      : replayId === ONBOARDING_DEMO_CURATED_ID
        ? ONBOARDING_REPLAY_SPEED
        : 1,
  );
  const [replaySkipped, setReplaySkipped] = useState(false);
  const [replayFinished, setReplayFinished] = useState(false);
  const [showReplayClickGuard, setShowReplayClickGuard] = useState(false);

  // Tour-hub: mark the matching stage complete the moment the curated
  // onboarding replay finishes playing.
  //
  //   • Standard run (`replayStartAtBeat` empty)        → "research"
  //   • Run started at `alert_welcome` (stage-3 entry)  → "alert"
  //
  // One-shot per replay run thanks to the boolean state — re-renders
  // never re-fire the dispatch. "Finished" here includes both natural
  // completion and skip-to-end (skipping also flips replayFinished=true).
  useEffect(() => {
    if (!replayFinished) return;
    if (replayId !== ONBOARDING_DEMO_CURATED_ID) return;
    dispatchStageComplete(
      replayStartAtBeat === "alert_welcome" ? "alert" : "research",
    );
  }, [replayFinished, replayId, replayStartAtBeat]);

  // Tour-hub: ALSO mark the stage complete the moment turn 0 finishes
  // streaming and the next_user_message handoff appears (i.e. the
  // viewer has watched the entire research turn and the buddy has
  // queued up the second prompt). Without this, a viewer who watches
  // the research turn end-to-end but bails BEFORE clicking Send on
  // the second prompt would never get credit — they only got credit
  // at full replay completion. Reaching the handoff is enough.
  //
  // `nextUserMessage` is computed synchronously from the SSE data
  // stream and flips truthy on every turn boundary; the ref guard
  // below ensures we fire exactly once per replay session even
  // though the value re-flips truthy at the start of turn 1.
  // Idempotent on the gate side anyway (markTourStageComplete dedupes),
  // but the ref keeps intent explicit and avoids extra dispatches.
  const tourStageHandoffFiredRef = useRef(false);
  useEffect(() => {
    if (replayId !== ONBOARDING_DEMO_CURATED_ID) return;
    if (!nextUserMessage) return;
    if (tourStageHandoffFiredRef.current) return;
    tourStageHandoffFiredRef.current = true;
    dispatchStageComplete(
      replayStartAtBeat === "alert_welcome" ? "alert" : "research",
    );
  }, [nextUserMessage, replayId, replayStartAtBeat]);

  // Sync the frontend's initial replaySpeed default to the backend.
  // Backend _replay_state defaults to speed=1.0 — without this POST,
  // our 2× default for guided-tour replays is purely cosmetic on the
  // speed pill and the actual stream still plays at 1×. We wait for
  // the first data item before firing so the server-side state entry
  // exists (otherwise /control returns 404). Fires exactly once per
  // replay session via a ref guard.
  const initialSpeedSyncedRef = useRef(false);
  useEffect(() => {
    if (!replayId) return;
    if (initialSpeedSyncedRef.current) return;
    if (!data || data.length === 0) return;
    if (replaySpeed === 1) {
      initialSpeedSyncedRef.current = true;
      return;
    }
    initialSpeedSyncedRef.current = true;
    fetch(`${API_BASE_URL}/replay/control`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: id, action: "set_speed", speed: replaySpeed }),
      keepalive: true,
    }).catch((err) => {
      console.warn("[replay] initial set_speed POST failed", err);
    });
  }, [replayId, data, replaySpeed, id]);
  // Feature flag: render a real-looking multimodal input bar in place
  // of the standard <ReplayControls> when this is on. Designed for
  // demo screen-recordings where the bottom of the page should look
  // identical to the live product. See `lib/demo-replay-config.ts`
  // for how the flag resolves (URL param > localStorage > env).
  const demoReplayInputOn = useDemoReplayInputEnabled();
  const onboardingReplayClickLockActive =
    replayId === ONBOARDING_DEMO_CURATED_ID &&
    !replayCleanMode &&
    !replayFinished &&
    !buddyBeat;
  const isAllowedOnboardingReplayClick = useCallback(
    (target: EventTarget | null) => {
      if (!(target instanceof Node)) return false;
      const targetElement =
        target instanceof Element ? target : target.parentElement;
      if (!targetElement) return false;
      if (
        targetElement.closest(
          "[data-buddy-overlay],[data-buddy-bubble='true'],[data-buddy-exit-confirm='true'],[data-replay-controls='true'],[data-onboarding-replay-click-guard='true']",
        )
      ) {
        return true;
      }
      if (
        nextUserMessage &&
        targetElement.closest("[data-demo-replay-input='true']")
      ) {
        return true;
      }
      return false;
    },
    [nextUserMessage],
  );

  useEffect(() => {
    if (!onboardingReplayClickLockActive) return;
    const blockOffScriptClick = (e: MouseEvent | PointerEvent) => {
      // Mobile fix: a touch-pointerdown fires the MOMENT the finger
      // lands on the screen, BEFORE the browser knows whether the
      // gesture is a tap or a scroll. If we run the off-script guard
      // on touch pointerdowns, EVERY scroll during the replay (which
      // is the natural mobile way to read streaming content) pops
      // the "Stay with the replay" modal. Skip touch pointerdowns;
      // genuine taps still get caught by the synthetic `mousedown`
      // and `click` events the browser dispatches only AFTER a touch
      // resolves as a tap. Mouse/pen pointerdowns stay guarded.
      // Same pattern as the BuddyOverlay off-target guard above.
      if (
        e.type === "pointerdown" &&
        (e as PointerEvent).pointerType === "touch"
      ) {
        return;
      }
      if (isAllowedOnboardingReplayClick(e.target)) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      setShowReplayClickGuard(true);
    };
    document.addEventListener("pointerdown", blockOffScriptClick, true);
    document.addEventListener("mousedown", blockOffScriptClick, true);
    document.addEventListener("click", blockOffScriptClick, true);
    return () => {
      document.removeEventListener("pointerdown", blockOffScriptClick, true);
      document.removeEventListener("mousedown", blockOffScriptClick, true);
      document.removeEventListener("click", blockOffScriptClick, true);
    };
  }, [isAllowedOnboardingReplayClick, onboardingReplayClickLockActive]);

  useEffect(() => {
    if (onboardingReplayClickLockActive) return;
    setShowReplayClickGuard(false);
  }, [onboardingReplayClickLockActive]);

  // Same flag family: when on, typing one of the configured keywords
  // (e.g. "ginger") in ANY live MultimodalInput — home page, sidebar
  // new-chat, mid-chat input — short-circuits the submit and pushes
  // the user into the matching curated replay instead of starting
  // a real agent run. This is what lets the founder type a code-name
  // during a recording and have the demo "magically" appear without
  // burning credits or polluting their chat history.
  // DemoReplayInput already does this for *replay mode*; this wrapper
  // covers the *live mode* path that DemoReplayInput never sees.
  //
  // IMPORTANT: We wrap BOTH `append` and `handleSubmit`. MultimodalInput's
  // `submitForm` actually uses `append()` to send messages (not
  // handleSubmit) — so wrapping handleSubmit alone was a no-op. We
  // still wrap handleSubmit too as a defensive belt-and-braces layer
  // for any code path that might submit a form directly.
  //
  // Flag is read SYNCHRONOUSLY on every submit (not via the React
  // hook) to dodge the useState-initial-false race that was making
  // the routing silently no-op on HMR remounts and on first paint.
  const tryKeywordRoute = useCallback(
    (text: string | undefined | null): boolean => {
      if (!readKeywordReplayRoutingEnabled()) return false;
      const route = findKeywordReplayRoute(text);
      if (!route) return false;
      setInput("");
      router.push(`/replay/${route.curatedId}`);
      return true;
    },
    [setInput, router],
  );

  const appendWithKeywordRouting = useCallback<typeof append>(
    (message, options) => {
      const text =
        typeof (message as any)?.content === "string"
          ? ((message as any).content as string)
          : "";
      if (tryKeywordRoute(text)) {
        return Promise.resolve(null);
      }
      return append(message, options);
    },
    [append, tryKeywordRoute],
  );

  const handleSubmitWithKeywordRouting = useCallback(
    (event?: any, options?: any) => {
      if (tryKeywordRoute(input)) {
        event?.preventDefault?.();
        return;
      }
      handleSubmit(event, options);
    },
    [tryKeywordRoute, input, handleSubmit],
  );

  // Sync the onboarding-replay's initial 1.5x speed to the backend after a
  // brief delay so the replay state is initialized before /replay/control
  // hits it. Without this, the first ~hundred ms of stream pace at 1x and
  // then jump to 1.5x — visible as a brief lag at the start of playback.
  useEffect(() => {
    if (replayId !== ONBOARDING_DEMO_CURATED_ID) return;
    const t = window.setTimeout(() => {
      fetch(`${API_BASE_URL}/replay/control`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          session_id: id,
          action: "set_speed",
          speed: ONBOARDING_REPLAY_SPEED,
        }),
      }).catch(() => {
        // Replay may not be initialized yet; the user can manually nudge
        // speed via the controls if this fails. Best-effort only.
      });
    }, 800);
    return () => window.clearTimeout(t);
  }, [replayId, id]);

  // ── Onboarding buddy: DISABLED ─────────────────────────────────────────
  // The four-phase wizard (idle pill → Continue spotlight → idle →
  // post-replay spotlight) has been intentionally removed for now —
  // priority is letting the demo itself shine: the replay streams,
  // switch_mode flips the artifact into alert mode mid-stream (now
  // wired through STREAMABLE_TOOLS in the backend), the alert canvas
  // fills with cells live, and upload_alert renders the success card
  // at the end. No popovers, no auto-navigation. The OnboardingTour
  // infrastructure is still mounted globally (app/layout.tsx) so it
  // can be re-enabled later by reintroducing the phase effects.
  //
  // NOTE: An earlier "backup completion signal" effect lived here,
  // flipping `replayFinished=true` whenever the AI SDK status went
  // streaming → ready. That effect was racey for multi-turn replays:
  // each turn is its own SSE request that ends with `d:` (status
  // flips to ready) AFTER the `2:[{type:next_user_message,...}]`
  // event. The data-watcher above and the status effect both ran on
  // the same post-stream tick, but useEffect closures don't see each
  // other's state updates within the same tick — so the backup saw
  // nextUserMessage=null and prematurely marked the replay finished
  // after turn 1, unmounting ReplayControls before the Continue
  // button could render. Result: replay dropped into the "Back to /"
  // CTA branch and turn 2 never started.
  // ReplayControls.onComplete (which fires only when isComplete
  // = !isStreaming && !nextUserMessage && hasEverStreamed) already
  // handles real completion correctly without this race.
  const { setArtifact } = useArtifact();
  const artifactVisibleFromState = useArtifactSelector(
    (state) => state.isVisible
  );
  const normalizedChatAlertLayoutRef = useRef<Set<string>>(new Set());

  // Normal /chat sessions can mount already in alert mode (sidebar open,
  // refresh, resume) without replaying the switch_mode tool result. Normalize
  // that path once so stale global artifact state from /alerts pages cannot
  // hide the chat on desktop.
  useLayoutEffect(() => {
    if (viewMode !== "alert" && viewMode !== "signal") return;
    if (isAlertsRoute || alertDoc || replayId) return;

    const key = `${id}:${isMobile ? "mobile" : "desktop"}`;
    if (normalizedChatAlertLayoutRef.current.has(key)) return;
    normalizedChatAlertLayoutRef.current.add(key);

    const nextIsChatMinimized = isMobile;
    telemetry.emit("alert_canvas.layout_state", {
      session_id: id,
      user_id: user?.uid,
      source: "alert_mode_mount_normalize",
      is_mobile: isMobile,
      is_visible: true,
      is_chat_minimized: nextIsChatMinimized,
      is_artifact_minimized: false,
    });

    setArtifact((prev) => {
      if (
        prev.isVisible === true &&
        prev.documentId === id &&
        prev.isChatMinimized === nextIsChatMinimized &&
        prev.isArtifactMinimized === false
      ) {
        return prev;
      }

      return {
        ...prev,
        isVisible: true,
        documentId: id,
        isChatMinimized: nextIsChatMinimized,
        isArtifactMinimized: false,
      };
    });
  }, [
    alertDoc,
    id,
    isAlertsRoute,
    isMobile,
    replayId,
    setArtifact,
    user?.uid,
    viewMode,
  ]);

  useEffect(() => {
    setArtifact((prev) =>
      prev.hideSteps === false ? prev : { ...prev, hideSteps: false }
    );
  }, [id, setArtifact]);

  // ── Onboarding "Ask the alert" bridge ───────────────────────────────────
  // The onboarding tour scene 2 (rendered on /alerts/[id]?onboarding=1)
  // walks the user past the chat input and offers a pre-filled question.
  // When the user clicks the buddy's CTA, the tour fires a window event —
  // we listen for it here and append the question to the live alert chat.
  //
  // Both owner AND visitor branches participate:
  //   • Visitor: route through the existing visitor submit gate so
  //     unauthenticated / free-tier users hit the same AuthModal /
  //     PlansModal paywall they'd see if they typed it themselves.
  //     That's the conversion moment.
  //   • Owner: skip the gate (owners have no paywall) and just append.
  //     Important because the canonical onboarding alert is owned by
  //     the founder account — when Mujtaba walks his own onboarding,
  //     the alert page fires the OWNER branch and visitor mode is off.
  //
  // Decoupled bus pattern so the tour component doesn't need a ref into
  // <Chat>.
  //
  // Side-effect: before appending we un-minimize the chat panel via the
  // artifact store so the user actually SEES their message and the
  // streaming response. The default alert-page state is canvas-first +
  // chat-minimized, which would render the user's question off-screen.
  useEffect(() => {
    if (!isAlertsRoute) return;
    const handler = (evt: Event) => {
      const ce = evt as CustomEvent<OnboardingAskAlertDetail>;
      const question = ce.detail?.question?.trim();
      if (!question) return;
      // Visitor-only gate: signed-out → AuthModal, free-tier → PlansModal.
      // For owners this branch is skipped entirely (no gate to clear).
      if (alertVisitorMode && onVisitorSubmitGate && !onVisitorSubmitGate()) {
        // Gate denied → modal already opened. Don't un-minimize the
        // chat — leaving the user staring at an empty expanded chat
        // after they close the modal would be a UX bug (mirrors the
        // pattern in artifact.tsx's wrappedAppend).
        return;
      }
      // eslint-disable-next-line no-console
      console.info("[onboarding-tour] suggested question fired", {
        question,
        alertVisitorMode,
        alertContextId,
      });
      setArtifact((prev) =>
        prev.isChatMinimized === false ? prev : { ...prev, isChatMinimized: false },
      );
      append({ role: "user", content: question });
    };
    window.addEventListener(ONBOARDING_ASK_ALERT_EVENT, handler);
    return () => window.removeEventListener(ONBOARDING_ASK_ALERT_EVENT, handler);
  }, [
    isAlertsRoute,
    alertVisitorMode,
    alertContextId,
    append,
    onVisitorSubmitGate,
    setArtifact,
  ]);

  // NOTE: the alert-page layout seeding (isVisible=true, isChatMinimized=true)
  // now lives in app/(chat)/alerts/[id]/page.tsx — it runs in a
  // useLayoutEffect BEFORE <Chat> mounts, so the store is already correct on
  // Chat's first render. A duplicate seed here was a no-op and used to cause
  // a ~46ms layout shift window on load. Removed 2026-04-20.

  // NOTE(alert-page cross-tab sync): previous attempt added an effect here
  // to `setMessages(initialMessages)` when SWR refetched a fresher server
  // session. That caused races — the length-grew trigger also fired our
  // Artifact auto-expand effect, which un-minimized the chat on landing.
  // Removed for now; alert page revalidates sessionData on focus, but the
  // Chat component's internal messages state only reflects what useChat
  // saw at mount. Good enough for single-tab use. Revisit when we design
  // a safer cross-tab sync (e.g. only merge additions, or use a "fresher
  // on server" indicator instead of auto-replacing).

  // Poll for sub-notebook progress during streaming to auto-open sidebar.
  //
  // Gated on `hasSubAgents` so demos/sessions without sub-agents pay
  // zero cost. Previously this polled every 2s for the entire duration
  // of every streaming session — 150-300 wasted HTTP roundtrips per
  // long replay, plus an SWR cache write + React invalidation each
  // time. `hasSubAgents` is known per-session at mount (curated
  // sessions set it from `sub_agent_snapshot`; live chats compute it
  // from the active run), so a cheap boolean gate here is correct.
  const hasAutoOpenedRef = useRef(false);
  const subProgressEndpoint = replayId
    ? `${API_BASE_URL}/replay/sub-notebook-progress/${id}`
    : `${API_BASE_URL}/strategies/sub-notebook-progress/${id}`;
  const shouldPollSubProgress =
    Boolean(hasSubAgents) && effectiveStatus === "streaming";
  const { data: subProgress } = useSWR(
    shouldPollSubProgress ? subProgressEndpoint : null,
    (url: string) => fetch(url).then(res => res.json()),
    { refreshInterval: 2000, revalidateOnFocus: false }
  );

  // Auto-open sidebar when sub-agents are detected.
  // Skipped in replay mode — the onboarding buddy owns canvas open/close
  // decisions (sub-agents during a tour should be an explicit buddy beat,
  // not an unannounced pop-open).
  useEffect(() => {
    if (replayId) return;
    if (
      subProgress?.cells_with_sub_agents?.length > 0 &&
      !artifactVisibleFromState &&
      !hasAutoOpenedRef.current
    ) {
      hasAutoOpenedRef.current = true;
      setArtifact((prev) => ({ ...prev, isVisible: true }));
    }
  }, [replayId, subProgress, artifactVisibleFromState, setArtifact]);

  const requestedDataRef = useRef<Array<RequestedDataSource>>([]);
  const [deepResearch, setDeepResearch] = useState(false);
  const deepResearchRef = useRef(false);
  useEffect(() => {
    deepResearchRef.current = deepResearch;
  }, [deepResearch]);
  const [modelTier, setModelTier] = useState<"basic" | "max">(initialModelTier ?? "max");
  const modelTierRef = useRef<"basic" | "max">(initialModelTier ?? "max");
  const modelTierStateRef = useRef<"basic" | "max">(initialModelTier ?? "max");
  useEffect(() => {
    modelTierRef.current = modelTier;
    modelTierStateRef.current = modelTier;
  }, [modelTier]);
  const usageDataRef = useRef<typeof usageData>(usageData);
  useEffect(() => { usageDataRef.current = usageData; }, [usageData]);

  // Load saved model tier preference once when usage data first arrives.
  // Default for everyone is now "max" (Pro / Claude). We INTENTIONALLY ignore
  // a saved pref of "basic" — free users can't message anyway, and paid
  // users should default to the better model. They can still toggle to
  // basic during the session via handleModelTierChange.
  const prefLoadedRef = useRef(false);
  useEffect(() => {
    if (prefLoadedRef.current) return;
    if (initialModelTier) {
      prefLoadedRef.current = true;
      setModelTier(initialModelTier);
      modelTierRef.current = initialModelTier;
      modelTierStateRef.current = initialModelTier;
      return;
    }
    const savedTier = usageData?.ui_prefs?.model_tier;
    if (savedTier === "max") {
      prefLoadedRef.current = true;
      setModelTier("max");
      modelTierRef.current = "max";
      modelTierStateRef.current = "max";
    } else if (usageData !== undefined) {
      // Either no saved pref, or saved as "basic" — keep the "max" default.
      prefLoadedRef.current = true;
    }
  }, [initialModelTier, usageData]);

  // Save preference whenever user explicitly changes the tier.
  // CRITICAL: also sync the refs synchronously so a submit firing in the
  // same tick (e.g. user clicks toggle then immediately presses Enter)
  // sees the new tier — `useEffect` only updates the ref *after* commit,
  // which caused stale "sent=basic state=max" mismatches in telemetry.
  const handleModelTierChange = useCallback(
    (tier: "basic" | "max") => {
      setModelTier(tier);
      modelTierRef.current = tier;
      modelTierStateRef.current = tier;
      if (user?.uid) {
        usageService.saveUserPreferences(user.uid, { model_tier: tier }).catch(() => {});
      }
    },
    [user?.uid]
  );

  // ── Specific model selection (Gemini / Sonnet 4.6 / Opus / Fable) ──
  // `modelTier` still drives billing + turn caps; this tracks WHICH model
  // within the tier the user picked, sent to the backend as `model_override`.
  const [selectedModelKey, setSelectedModelKey] = useState<AgentModelKey>(
    () => modelKeyFromTier(initialModelTier ?? "max")
  );
  const modelOverrideRef = useRef<string | undefined>(
    modelByKey(modelKeyFromTier(initialModelTier ?? "max")).slug
  );

  const handleModelChange = useCallback(
    (key: AgentModelKey) => {
      const model = modelByKey(key);
      setSelectedModelKey(key);
      modelOverrideRef.current = model.slug;
      // Keep the billing tier + saved pref in sync with the chosen model.
      handleModelTierChange(model.tier);
      // Remember the exact model on this device so reloads keep it.
      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem(SELECTED_MODEL_STORAGE_KEY, key);
        } catch {
          /* ignore quota / privacy-mode errors */
        }
      }
    },
    [handleModelTierChange]
  );

  // Let out-of-chat UI (e.g. the credits-exhausted card's "Use Gemini free"
  // button) switch the composer's model without threading props through the
  // whole message tree. The retry variant also dismisses the error card and
  // auto-resends the message that was blocked, on the new model.
  useEffect(() => {
    const onSwitch = (e: Event) => {
      const key = (e as CustomEvent).detail?.key;
      if (isAgentModelKey(key)) handleModelChange(key);
    };
    const onRetry = (e: Event) => {
      const key = (e as CustomEvent).detail?.key;
      if (isAgentModelKey(key)) handleModelChange(key);
      // Drop the error-card message(s) so the card disappears, then resend
      // the blocked user message. modelOverrideRef is already updated
      // synchronously by handleModelChange; the short defer lets React
      // re-render so model_tier in prepareRequestBody is fresh too.
      setMessages((msgs) => msgs.filter((m) => !(m as any).errorData));
      setTimeout(() => reload(), 80);
    };
    window.addEventListener("xynth:switch-model", onSwitch);
    window.addEventListener("xynth:retry-with-model", onRetry);
    return () => {
      window.removeEventListener("xynth:switch-model", onSwitch);
      window.removeEventListener("xynth:retry-with-model", onRetry);
    };
  }, [handleModelChange, setMessages, reload]);

  // Remember last used model across reloads. A URL `?model_tier=` intent
  // (initialModelTier) wins for that one navigation; otherwise restore the
  // model saved on this device. This is what fixes "it always changes back
  // to Claude" — the previous code intentionally discarded the saved choice.
  const modelLoadedRef = useRef(false);
  useEffect(() => {
    if (modelLoadedRef.current) return;
    modelLoadedRef.current = true;
    // Skill launch with an explicit model wins over everything for this nav.
    if (initialModelKey && isAgentModelKey(initialModelKey)) {
      const model = modelByKey(initialModelKey);
      setSelectedModelKey(initialModelKey);
      modelOverrideRef.current = model.slug;
      setModelTier(model.tier);
      modelTierRef.current = model.tier;
      modelTierStateRef.current = model.tier;
      return;
    }
    if (initialModelTier) {
      const key = modelKeyFromTier(initialModelTier);
      setSelectedModelKey(key);
      modelOverrideRef.current = modelByKey(key).slug;
      return;
    }
    if (typeof window !== "undefined") {
      let saved: string | null = null;
      try {
        saved = window.localStorage.getItem(SELECTED_MODEL_STORAGE_KEY);
      } catch {
        saved = null;
      }
      if (isAgentModelKey(saved)) {
        const model = modelByKey(saved);
        setSelectedModelKey(saved);
        modelOverrideRef.current = model.slug;
        setModelTier(model.tier);
        modelTierRef.current = model.tier;
        modelTierStateRef.current = model.tier;
      }
    }
  }, [initialModelTier, initialModelKey]);

  // Skill launch: raise the turn cap on the FIRST request only. Cleared in the
  // request builder right after it's read so the rest of the chat is normal.
  const skillRunRef = useRef<boolean>(Boolean(initialSkillRun));
  // Skill launch (no-blob): the composer sets this to the active skill id right
  // before append; the request builder reads-and-clears it into `skill_id` so
  // the backend resolves + injects the instruction (visible message stays the
  // user's own input).
  const activeSkillIdRef = useRef<string[] | undefined>(undefined);
  const subAgentMessageIdsRef = useRef<Set<string>>(new Set());
  // message id -> skill id, for live skill-artifact card render before reload.
  const messageSkillsRef = useRef<Map<string, string[]>>(new Map());

  // ── "Turn this chat into a skill" (the per-message "Save as skill" action) ──
  // Arms the create-a-skill meta-skill on the NEXT message in the CURRENT chat,
  // so the skill builder sees the whole conversation as context and drafts from
  // the work we just did (instead of starting a blank interview).
  const handleSaveChatAsSkill = useCallback(() => {
    // skillRunRef → extended_turns (skill interviews can be long); no planner.
    skillRunRef.current = true;
    // activeSkillIdRef → skill_id, so the backend injects the create-a-skill
    // instruction + skill-builder tools for this one message.
    activeSkillIdRef.current = ["create-a-skill"];
    append({
      role: "user",
      content:
        "Turn this whole conversation into a reusable skill I can run again with different inputs. Base it on the work we just did above.",
    });
  }, [append]);

  // NOTE: "Suggest a change" on the draft card no longer appends a new chat
  // message. propose_skill now BLOCKS the agent (like ask_user), so the card
  // releases the waiting turn via POST /agent/propose-skill-reply with the
  // feedback text and the agent re-drafts in-place. See propose-skill/display.

  useEffect(() => {
    const previousStatus = statusTransitionRef.current;
    statusTransitionRef.current = status;

    if (status === "submitted" && previousStatus !== "submitted") {
      const startedAtMs = Date.now();
      const pendingMeta = pendingStreamTelemetryRef.current;
      pendingDropRecoveryRequestIdRef.current = null;
      attemptedDropRecoveryRequestIdRef.current = null;
      liveStreamTelemetryRef.current = {
        requestId: pendingMeta.requestId,
        startedAtMs,
        startedAtIso: new Date(startedAtMs).toISOString(),
        lastActivityAtMs: startedAtMs,
        lastChunkId: null,
        chunkOffsetsMs: [],
        heartbeatOffsetsMs: [],
        dropOffsetsMs: [],
        visibilityEvents: [],
        networkEvents: [],
        maxGapMs: 0,
        meta: {
          agentType: pendingMeta.agentType,
          modelTier: pendingMeta.modelTier,
          deepResearch: pendingMeta.deepResearch,
          hasAttachments: pendingMeta.hasAttachments,
          messageLen: pendingMeta.messageLen,
          isReplay: pendingMeta.isReplay,
        },
      };

      telemetry.streamStart({
        session_id: id,
        user_id: user?.uid,
        request_id: pendingMeta.requestId ?? undefined,
        started_at_client_iso: liveStreamTelemetryRef.current.startedAtIso,
        agent_type: pendingMeta.agentType,
        model_tier: pendingMeta.modelTier,
        model_tier_state: pendingMeta.modelTierState ?? undefined,
        model_tier_saved_pref: pendingMeta.modelTierSavedPref ?? undefined,
        model_tier_mismatch:
          (pendingMeta.modelTierState !== undefined && pendingMeta.modelTierState !== pendingMeta.modelTier) ||
          (pendingMeta.modelTierSavedPref !== undefined &&
            pendingMeta.modelTierSavedPref !== null &&
            pendingMeta.modelTierSavedPref !== pendingMeta.modelTier),
        deep_research: pendingMeta.deepResearch,
        has_attachments: pendingMeta.hasAttachments,
        message_len: pendingMeta.messageLen,
        is_replay: pendingMeta.isReplay,
      });
    }

    if (
      (previousStatus === "submitted" || previousStatus === "streaming") &&
      status !== "submitted" &&
      status !== "streaming" &&
      liveStreamTelemetryRef.current.startedAtMs
    ) {
      finishLiveStream("abort", {
        abort_source: liveStreamAbortSourceRef.current ?? "status_transition",
      });
    }
  }, [finishLiveStream, id, status, user?.uid]);

  useEffect(() => {
    if (!pendingDropRecoveryRequestIdRef.current) {
      return;
    }
    if (
      status === "submitted" ||
      status === "streaming" ||
      isRecovering ||
      isResumeStreaming
    ) {
      return;
    }
    const triggerRequestId = pendingDropRecoveryRequestIdRef.current;
    pendingDropRecoveryRequestIdRef.current = null;
    // Hop beacon (July 2026): proves chat.tsx effect fired recoverNow() —
    // sits between error_routed_to_recovery and use-auto-resume recovery_started.
    telemetry.emit("stream.recovery_trigger", {
      session_id: id,
      user_id: user?.uid,
      request_id: triggerRequestId ?? undefined,
      recovery_version: "v3",
    });
    recoverNow();
  }, [id, isRecovering, isResumeStreaming, recoverNow, status, user?.uid]);

  // Fix (reliability v3): ask the backend whether the request is actually
  // still running. Authoritative — immune to the activity-clock resets that
  // blind the v2 watchdog under rapid tab-switching. If the backend says the
  // request is finished but we're still "streaming", trigger the standard
  // drop-recovery path (which rehydrates the saved answer from the DB).
  const checkBackendRequestStatus = useCallback(
    (trigger: "visibility_return" | "watchdog_stale") => {
      if (replayId) return;
      if (status !== "submitted" && status !== "streaming") return;
      const run = liveStreamTelemetryRef.current;
      const requestId = run.requestId;
      if (!requestId || !run.startedAtMs) return;
      if (isRecovering || isResumeStreaming) return;
      if (
        pendingDropRecoveryRequestIdRef.current === requestId ||
        attemptedDropRecoveryRequestIdRef.current === requestId
      ) {
        return;
      }
      const nowMs = Date.now();
      if (statusCheckInFlightRef.current) return;
      if (nowMs - lastStatusCheckMsRef.current < 15000) return;
      statusCheckInFlightRef.current = true;
      lastStatusCheckMsRef.current = nowMs;

      const url = new URL(`${API_BASE_URL}/agent/request-status`);
      url.searchParams.set("session_id", id);
      url.searchParams.set("request_id", requestId);
      // HARD TIMEOUT (July 2026, session 7313695e deep-dive): same class of
      // bug as the recovery-pipeline status fetch — no timeout meant a dead
      // connection left statusCheckInFlightRef stuck true forever, silently
      // disabling ALL future backend status checks for the stream.
      const probeStartedAtMs = Date.now();
      fetch(url.toString(), {
        credentials: "include",
        signal: AbortSignal.timeout(10000),
      })
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(`request-status ${response.status}`);
          }
          const payload = (await response.json()) as {
            status: string;
            request_id: string | null;
          };
          const finished = payload.status !== "running";
          telemetry.emit("stream.backend_status_check", {
            session_id: id,
            user_id: user?.uid,
            request_id: requestId,
            trigger,
            backend_status: payload.status,
            stream_finished_serverside: finished,
            duration_ms: Date.now() - (run.startedAtMs ?? Date.now()),
            recovery_version: "v3",
          });
          if (
            finished &&
            pendingDropRecoveryRequestIdRef.current !== requestId &&
            attemptedDropRecoveryRequestIdRef.current !== requestId
          ) {
            // Backend already finished this request — the live SSE is dead.
            // Route into the standard drop-recovery flow to load the saved
            // answer instead of spinning forever.
            attemptedDropRecoveryRequestIdRef.current = requestId;
            pendingDropRecoveryRequestIdRef.current = requestId;
            liveStreamAbortSourceRef.current = "drop_recovery";
            stop();
          }
        })
        .catch((error) => {
          // Network blip — next throttled check will retry. Never escalate
          // a failed status probe into a user-visible error. But DO record
          // it: a probe that fails while the backend is reachable for
          // telemetry is exactly the signature we needed for session
          // 7313695e and didn't have.
          telemetry.emit("stream.backend_status_check", {
            session_id: id,
            user_id: user?.uid,
            request_id: requestId,
            trigger,
            backend_status: "probe_error",
            error_message:
              error instanceof Error ? error.message : String(error),
            latency_ms: Date.now() - probeStartedAtMs,
            recovery_version: "v3",
          });
        })
        .finally(() => {
          statusCheckInFlightRef.current = false;
        });
    },
    [id, isRecovering, isResumeStreaming, replayId, status, stop, user?.uid],
  );

  useEffect(() => {
    const handler = () => {
      if (status !== "submitted" && status !== "streaming") {
        return;
      }

      const run = liveStreamTelemetryRef.current;
      if (!run.startedAtMs) {
        return;
      }

      const nowMs = Date.now();
      const event = {
        offset_ms: nowMs - run.startedAtMs,
        at_iso: new Date(nowMs).toISOString(),
        visibility_state: document.visibilityState,
      };
      run.visibilityEvents.push(event);
      telemetry.streamVisibility({
        session_id: id,
        user_id: user?.uid,
        request_id: run.requestId ?? undefined,
        visibility_state: document.visibilityState,
        stream_active: true,
        duration_ms: nowMs - run.startedAtMs,
        offset_ms: event.offset_ms,
      });

      // Fix (reliability v3): if we return to a stream that has been silent
      // for a while, ask the backend for the truth BEFORE resetting the
      // activity clock. Rapid tab-switching used to reset the clock on every
      // return, so the v2 watchdog never saw 30s of visible inactivity and a
      // dead stream was never recovered.
      const staleMs =
        run.lastActivityAtMs !== null ? nowMs - run.lastActivityAtMs : 0;
      if (document.visibilityState === "visible" && staleMs >= 15000) {
        checkBackendRequestStatus("visibility_return");
      }

      // Fix #1 (reliability v2): returning to the foreground grants a fresh
      // activity window. While hidden the browser throttles the SSE reader, so
      // the server's 5s heartbeats buffer up unread; resetting lastActivityAtMs
      // here gives them a clean 30s to flush before the drop-watchdog can
      // declare a (false) drop on tab return.
      if (document.visibilityState === "visible" && run.lastActivityAtMs !== null) {
        run.lastActivityAtMs = nowMs;
      }
    };

    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [checkBackendRequestStatus, id, status, user?.uid]);

  useEffect(() => {
    const onOnline = () => {
      const run = liveStreamTelemetryRef.current;
      if ((status !== "submitted" && status !== "streaming") || !run.startedAtMs) {
        return;
      }

      const nowMs = Date.now();
      const event = {
        offset_ms: nowMs - run.startedAtMs,
        at_iso: new Date(nowMs).toISOString(),
        is_online: true,
      };
      run.networkEvents.push(event);
      telemetry.streamNetwork({
        session_id: id,
        user_id: user?.uid,
        request_id: run.requestId ?? undefined,
        is_online: true,
        stream_active: true,
        duration_ms: nowMs - run.startedAtMs,
        offset_ms: event.offset_ms,
      });
    };

    const onOffline = () => {
      const run = liveStreamTelemetryRef.current;
      if ((status !== "submitted" && status !== "streaming") || !run.startedAtMs) {
        return;
      }

      const nowMs = Date.now();
      const event = {
        offset_ms: nowMs - run.startedAtMs,
        at_iso: new Date(nowMs).toISOString(),
        is_online: false,
      };
      run.networkEvents.push(event);
      telemetry.streamNetwork({
        session_id: id,
        user_id: user?.uid,
        request_id: run.requestId ?? undefined,
        is_online: false,
        stream_active: true,
        duration_ms: nowMs - run.startedAtMs,
        offset_ms: event.offset_ms,
      });
    };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [id, status, user?.uid]);

  useEffect(() => {
    if (status !== "submitted" && status !== "streaming") {
      return;
    }

    // Replay streams don't emit redis_chunk_id / heartbeat data parts, so
    // `lastActivityAtMs` would never advance past `startedAtMs` — after 30s
    // the drop-watchdog would call stop() and silently abort the replay
    // mid-narration (e.g. cutting off the final wrap-up assistant message).
    // Replay can't be auto-recovered (useAutoResume short-circuits on
    // replayId) so there's nothing useful to do here. Skip entirely.
    if (replayId) {
      return;
    }

    const interval = window.setInterval(() => {
      const run = liveStreamTelemetryRef.current;
      if (!run.startedAtMs || run.lastActivityAtMs === null) {
        return;
      }

      const nowMs = Date.now();
      const lastChunkAgeMs = nowMs - run.lastActivityAtMs;

      // Fix (reliability v3): once the stream has been quiet for 20s, ask the
      // backend directly whether the request is still running (throttled to
      // one probe per 15s inside the callback). This works even while hidden
      // and is immune to the visibility-return clock resets below — it's the
      // safety net for connections that died without the watchdog noticing.
      if (lastChunkAgeMs >= 20000) {
        checkBackendRequestStatus("watchdog_stale");
      }

      if (lastChunkAgeMs < 30000) {
        return;
      }

      // Fix #1 (reliability v2): never declare a drop while the tab is hidden.
      // Backgrounded tabs throttle the SSE reader so the server's 5s heartbeats
      // buffer up unread — the gap is a browser artifact, not a dead stream.
      // ~65% of v1 drops fired on hidden tabs and most "recovered" themselves
      // on return. We just log a suppressed event (throttled) and wait; the
      // visibilitychange handler resets the activity clock when the tab
      // returns, giving buffered heartbeats a fresh window to flush.
      if (document.visibilityState !== "visible") {
        if (nowMs - lastHiddenSuppressMsRef.current >= 15000) {
          lastHiddenSuppressMsRef.current = nowMs;
          telemetry.emit("stream.drop_suppressed_hidden", {
            session_id: id,
            user_id: user?.uid,
            request_id: run.requestId ?? undefined,
            duration_ms: nowMs - run.startedAtMs,
            seconds_since_last_chunk: Math.round(lastChunkAgeMs / 1000),
            visibility_state: document.visibilityState,
            recovery_version: "v2",
          });
        }
        return;
      }

      const offsetMs = nowMs - run.startedAtMs;
      const lastDropOffset = run.dropOffsetsMs[run.dropOffsetsMs.length - 1];
      if (
        typeof lastDropOffset === "number" &&
        Math.abs(lastDropOffset - offsetMs) < 5000
      ) {
        return;
      }

      run.dropOffsetsMs.push(offsetMs);
      const requestId = run.requestId;
      const shouldAttemptRecovery =
        Boolean(requestId) &&
        navigator.onLine &&
        document.visibilityState === "visible" &&
        !isRecovering &&
        !isResumeStreaming &&
        pendingDropRecoveryRequestIdRef.current !== requestId &&
        attemptedDropRecoveryRequestIdRef.current !== requestId;
      telemetry.streamDrop({
        session_id: id,
        user_id: user?.uid,
        request_id: requestId ?? undefined,
        duration_ms: offsetMs,
        seconds_since_last_chunk: Math.round(lastChunkAgeMs / 1000),
        chunks_received: run.chunkOffsetsMs.length,
        visibility_state: document.visibilityState,
        is_online: navigator.onLine,
        last_chunk_age_ms: lastChunkAgeMs,
        auto_recovery_attempted: shouldAttemptRecovery,
      });
      if (shouldAttemptRecovery && requestId) {
        attemptedDropRecoveryRequestIdRef.current = requestId;
        pendingDropRecoveryRequestIdRef.current = requestId;
        liveStreamAbortSourceRef.current = "drop_recovery";
        stop();
      }
    }, 5000);

    return () => window.clearInterval(interval);
  }, [checkBackendRequestStatus, id, isRecovering, isResumeStreaming, replayId, status, stop, user?.uid]);

  const removeAttachment = async (attachmentToRemove: Attachment) => {
    try {
      const lowerName = (attachmentToRemove.name || "").toLowerCase();
      const isIngestDoc =
        lowerName.endsWith(".pdf") ||
        lowerName.endsWith(".txt") ||
        lowerName.endsWith(".docx");

      if (isIngestDoc) {
        const docId = attachmentToRemove.url;
        if (!docId) throw new Error("Missing document ID for deletion");
        await apiClient.deleteIngestDoc(docId);
      } else {
        const filePath = attachmentToRemove.name;
        if (!filePath) {
          throw new Error("Attachment name is required for deletion");
        }
        await apiClient.deleteFile(filePath);
      }

      setAttachments((currentAttachments) =>
        currentAttachments.filter(
          (attachment) => attachment.url !== attachmentToRemove.url
        )
      );
    } catch (error) {
      toast({
        type: "error",
        description: "Failed to remove file. Please try again.",
      });
    }
  };

  // Count completed add_cell tool results across all messages
  const addCellResultCount = useMemo(() => {
    let count = 0;
    for (const msg of messages) {
      if (msg.role === "assistant") {
        for (const part of (msg.parts ?? []) as any[]) {
          if (
            part.type === "tool-invocation" &&
            part.toolInvocation?.state === "result" &&
            part.toolInvocation?.toolName === "add_cell"
          ) {
            count++;
          }
        }
      }
    }
    return count;
  }, [messages]);

  const hasAddCellResult = addCellResultCount > 0;

  const isForkedWorkflow = Boolean(sourceNotebookId);

  // Artifact auto-open logic
  useEffect(() => {
    // Replay-mode auto-open is intentionally disabled: the onboarding
    // buddy (see docs/onboarding-buddy.md once it lands) drives when
    // the canvas opens so the tour starts on the chat shell and the
    // first cell appearance is a highlighted buddy moment, not a
    // silent pop-open. Only forked-workflow auto-open survives here.
    const shouldAutoOpen = isForkedWorkflow && !replayId;

    if (shouldAutoOpen && !hasAutoOpenedArtifact) {
      const shouldBeVisible = isMobile ? false : true;
      setArtifact((prev) => ({
        ...prev,
        isVisible: shouldBeVisible,
        documentId: id,
      }));
      if (!isMobile) {
        setHasAutoOpenedArtifact(true);
      }
    } else if (!shouldAutoOpen && isFirstArtifactCheckRef.current) {
      // /alerts/[id] routes have already set isVisible=true via the alert
      // mount effect above — don't reset it here or the page falls back
      // to the chat-empty landing branch.
      if (!alertDoc) {
        setArtifact((prev) => {
          if (!prev.isVisible) return prev;
          return { ...prev, isVisible: false };
        });
      }
    }
    isFirstArtifactCheckRef.current = false;
  }, [
    setArtifact,
    id,
    messages.length,
    hasAddCellResult,
    isForkedWorkflow,
    isMobile,
    hasAutoOpenedArtifact,
    alertDoc,
  ]);

  // Branch trace: on alert pages, which top-level JSX branch does <Chat>
  // return on each render? If artifactVisibleFromState is false, we return
  // the "chat shell" branch (ChatHeader + chat-only body), which is what
  // mobile was landing on — the canvas never mounted. If true, we return
  // the <Artifact> branch and the canvas renders. One log per render.
  if (alertDoc) {
    timingLog("chat:branch", {
      t: tSinceNav(),
      artifactVisibleFromState,
      isMobile,
      branch: artifactVisibleFromState ? "artifact" : "chat_shell",
    });
  }

  const isHomeHero =
    pathname === "/" &&
    messages.length === 0 &&
    !artifactVisibleFromState;

  return (
    <div
      className="flex flex-col min-w-0 h-dvh md:h-dvh"
      data-replay-mode={replayId ? "true" : undefined}
    >
      <title>{title && title !== "New Chat" ? `${title} | Xynth` : "Xynth - AI Market Analysis Agent"}</title>
      {/* Onboarding-buddy overlay (replay-only). The component itself is
          a portal to document.body, so its position in this tree only
          matters for prop wiring; rendering at the top of the return
          keeps it visible across both chat-only and artifact branches. */}
      {replayId ? (
        <BuddyOverlay beat={buddyBeat} onContinue={releaseBuddyBeat} onExit={handleBuddyExit} />
      ) : null}
      {showReplayClickGuard ? (
        <div
          className="fixed inset-0 z-[190] flex items-center justify-center bg-black/70 px-4"
          data-onboarding-replay-click-guard="true"
          role="dialog"
          aria-modal="true"
          aria-labelledby="onboarding-replay-click-guard-title"
        >
          <div className="w-full max-w-md rounded-2xl border border-white/15 bg-zinc-950 px-6 py-5 text-white shadow-2xl">
            <div
              id="onboarding-replay-click-guard-title"
              className="text-lg font-semibold tracking-tight"
            >
              Stay with the replay
            </div>
            <div className="mt-4 space-y-3 text-sm leading-6 text-zinc-300">
              <p>
                This is a guided walkthrough of{" "}
                <strong className="font-semibold text-white">how Xynth works</strong>.
              </p>
              <p>
                Watch the next step, then tap the{" "}
                <strong className="font-semibold text-white">highlighted action</strong>{" "}
                when it appears.
              </p>
            </div>
            <div className="mt-6 flex items-center justify-between gap-3">
              {/* Escape hatch — the replay is a guided onboarding flow
                  but we can't force anyone to sit through it. "Exit
                  replay" dismisses the guard and routes home, which
                  unmounts the replay session entirely. The user can
                  always re-launch the tour from the "Onboarding to
                  Xynth" card on /automations. */}
              <button
                type="button"
                onClick={() => {
                  setShowReplayClickGuard(false);
                  router.push("/");
                }}
                className="text-sm font-medium text-zinc-400 transition hover:text-zinc-200"
              >
                Exit replay
              </button>
              <button
                type="button"
                onClick={() => setShowReplayClickGuard(false)}
                className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-black transition hover:bg-white/90"
                autoFocus
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {artifactVisibleFromState ? (
        <div className="flex-1 overflow-hidden">
          <Artifact
            chatId={id}
            activeRunSessionId={activeRunSessionId}
            viewMode={viewMode}
            alertStartCellIndex={alertStartCellIndex}
            hasAlertDoc={Boolean(alertDoc)}
            alertIsOwner={Boolean(alertIsOwner)}
            alertIsSubscribed={Boolean(alertIsSubscribed)}
            alertId={alertDoc?._id ?? null}
            alertName={alertDoc?.name ?? null}
            alertExplanationMd={alertDoc?.explanation_md ?? null}
            alertScheduleText={alertDoc?.schedule_description || alertDoc?.schedule || null}
            alertNotificationEmail={alertDoc?.notification_email ?? null}
            alertPresentedBy={alertPresentedBy ?? null}
            isAlertsRoute={isAlertsRoute}
            pageContextRef={pageContextRef}
            cellDescriptions={alertDoc?.cell_descriptions ?? null}
            alertVisitorMode={alertVisitorMode}
            onVisitorSubmitGate={onVisitorSubmitGate}
            alertHeader={
              alertDoc && (viewMode === "alert" || viewMode === "signal") ? (
                <AlertTopBar
                  alert={alertDoc}
                  onMutated={onAlertMutated ?? (() => {})}
                  onDeleted={onAlertDeleted ?? (() => {})}
                  isReadonly={!alertIsOwner}
                  viewMode={viewMode}
                  subscribedEmail={alertSubscribedEmail ?? null}
                  subscriberCount={alertSubscriberCount}
                />
              ) : undefined
            }
            input={input}
            setInput={setInput}
            handleSubmit={handleSubmitWithKeywordRouting}
            status={effectiveStatus}
            stop={effectiveStop}
            attachments={attachments}
            setAttachments={setAttachments}
            append={appendWithKeywordRouting}
            messages={messages}
            setMessages={setMessages}
            reload={reload}
            votes={votes}
            isReadonly={isReadonly}
            readonlyReason={readonlyReason}
            replayId={replayId}
            replaySessionId={id}
            replayIsStreaming={effectiveStatus === "streaming" || effectiveStatus === "submitted"}
            replayNextUserMessage={nextUserMessage}
            onReplayContinue={(msg: string) => {
              // Mark all current next_user_message events as consumed —
              // the cursor advances past `data.length`, so useMemo returns
              // null until a NEW emission appears (next turn's end).
              setNextUserMessageConsumedAt(data?.length ?? 0);
              // Treat the Continue click as a "handoff" event — this is
              // when the tour transitions from one turn to the next
              // (research → alert handoff, or alert → publish). Captures
              // funnel progress between major tour phases without
              // depending on a specific beat_id name.
              if (replayId) {
                telemetry.emit("replay.handoff_to_chat", {
                  session_id: id,
                  curated_id: replayId,
                  message_preview: msg.slice(0, 80),
                });
              }
              append({ role: "user", content: msg });
            }}
            replaySpeed={replaySpeed}
            onReplaySpeedChange={setReplaySpeed}
            replaySkipped={replaySkipped}
            onReplaySkippedChange={setReplaySkipped}
            replayPublishGate={publishGate}
            replayCleanMode={replayCleanMode}
            onReplayPublishConfirm={() => {
              // Pair event to `replay.publish_gate_shown`. Funnel:
              // shown→resumed gives us the publish-button conversion
              // rate, the most important single click in the tour.
              if (replayId) {
                telemetry.emit("replay.publish_gate_resumed", {
                  session_id: id,
                  curated_id: replayId,
                  alert_name: publishGate?.alertName ?? null,
                });
              }
              setPublishGateConsumedAt(data?.length ?? 0);
            }}
            selectedVisibilityType={visibilityType}
            dataRef={requestedDataRef}
            deepResearchRef={deepResearchRef}
            modelTierRef={modelTierRef}
            modelTier={modelTier}
            onModelTierChange={handleModelTierChange}
            selectedModelKey={selectedModelKey}
            onModelChange={handleModelChange}
            modelOverrideRef={modelOverrideRef}
            subAgentMessageIdsRef={subAgentMessageIdsRef}
            section={section}
            title={messages.length > 0 && !title ? "New Chat" : title || ""}
            isForkedWorkflow={isForkedWorkflow}
          />
        </div>
      ) : (
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
          {isHomeHero && (
            <div className="pointer-events-none absolute inset-0 z-0 animate-[fadeIn_600ms_ease-out_100ms_both]">
              <Dither
                waveColor={[0.3, 0.3, 0.3]}
                disableAnimation={false}
                enableMouseInteraction={!isMobile}
                mouseRadius={0.3}
                colorNum={5}
                waveAmplitude={0.2}
                waveFrequency={4}
                waveSpeed={0.01}
              />
            </div>
          )}
          <ChatHeader
            chatId={id}
            selectedVisibilityType={initialVisibilityType}
            isReadonly={isReadonly}
            section={section}
            title={messages.length > 0 && !title ? "New Chat" : title || ""}
            messages={messages}
            hasMessages={messages.length > 0}
            append={append}
            isStreaming={
              effectiveStatus === "streaming" ||
              effectiveStatus === "submitted"
            }
            viewMode={viewMode}
            overlayVariant={isHomeHero ? "hero" : "default"}
          />
          <div
            className={cn(
              "relative z-10 mx-2 flex flex-1 flex-col",
              messages.length > 0 && "overflow-y-scroll",
            )}
          >
          {messages.length === 0 && replayId ? (
            <div className="flex-1 flex flex-col items-start gap-4 max-w-3xl mx-auto w-full px-4 pt-8">
              <div className="h-4 w-48 bg-zinc-800/30 rounded-md animate-pulse" />
              <div className="flex flex-col gap-2 w-full max-w-md">
                <div className="h-3 w-full bg-zinc-800/20 rounded-md animate-pulse" />
                <div className="h-3 w-3/4 bg-zinc-800/15 rounded-md animate-pulse" />
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="relative flex min-h-screen flex-col">
              <div className="relative z-10 flex shrink-0 flex-col items-center justify-center px-4 pb-6 pt-16 md:pb-8 md:pt-24">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.85)_0%,rgba(0,0,0,0.5)_40%,transparent_75%)] pointer-events-none" />
                <div className="w-full max-w-3xl relative overflow-hidden">
                  <Messages
                    chatId={id}
                    status={effectiveStatus}
                    votes={votes}
                    messages={messages}
                    setMessages={setMessages}
                    reload={reload}
                    isReadonly={isReadonly}
                    append={append}
                    subAgentMessageIdsRef={subAgentMessageIdsRef}
                    messageSkillsRef={messageSkillsRef}
                    viewMode={viewMode}
                    initialViewMode={initialViewMode}
                    isAlertsRoute={isAlertsRoute}
                    alertStartCellIndex={alertStartCellIndex}
                    onSaveAsSkill={handleSaveChatAsSkill}
                  />
                  {showReloadChat && (
                    <div className="flex justify-center my-3">
                      <button
                        type="button"
                        onClick={handleReloadChat}
                        className="rounded-lg border border-zinc-700 bg-zinc-900 px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-800 transition-colors"
                      >
                        Connection interrupted — tap to reload chat
                      </button>
                    </div>
                  )}
                  {!isReadonly && (
                    <form className="w-full mt-6">
                      <MultimodalInput
                        chatId={id}
                        input={input}
                        setInput={setInput}
                        handleSubmit={handleSubmitWithKeywordRouting}
                        status={effectiveStatus}
                        stop={effectiveStop}
                        attachments={attachments}
                        setAttachments={setAttachments}
                        onRemoveAttachment={removeAttachment}
                        messages={messages}
                        setMessages={setMessages}
                        append={appendWithKeywordRouting}
                        selectedVisibilityType={visibilityType}
                        dataRef={requestedDataRef}
                        hideData={false}
                        deepResearchRef={deepResearchRef}
                        deepResearch={deepResearch}
                        modelTierRef={modelTierRef}
                        modelTier={modelTier}
                        onModelTierChange={handleModelTierChange}
                        selectedModelKey={selectedModelKey}
                        onModelChange={handleModelChange}
                        modelOverrideRef={modelOverrideRef}
                        activeSkillIdRef={activeSkillIdRef}
                        initialActiveSkill={
                          initialUserQuery ? undefined : initialActiveSkill
                        }
                        onDeepResearchChange={setDeepResearch}
                        onStopped={effectiveStop}
                        // Homepage-only Cmd/Ctrl+Enter shortcut: route the
                        // visitor into the no-buddy "clean" replay of the
                        // onboarding tour. Plain Enter still submits the
                        // typed message into a real chat. Gating on
                        // pathname keeps this shortcut off every other
                        // surface that mounts the same composer.
                        onCmdEnter={
                          pathname === "/"
                            ? (draftText: string) => {
                                // Forward the typed text as `initial_prompt`
                                // so the clean replay's first user bubble
                                // reads as what the visitor wrote, not the
                                // recording's canned first user message.
                                // Empty draft → no override, falls back to
                                // the recorded first user message.
                                const base = `/replay/${ONBOARDING_DEMO_CURATED_ID}/clean`;
                                const url = draftText
                                  ? `${base}?initial_prompt=${encodeURIComponent(draftText)}`
                                  : base;
                                router.push(url);
                              }
                            : undefined
                        }
                      />
                    </form>
                  )}

                  {/* Self-hosted ticker tape, gated to `/` so it never
                      appears on /chat/[id] or other routes. */}
                  {pathname === "/" && (
                    <div className="w-full mt-4">
                      <XynthTickerTape symbols={TICKER_TAPE_SYMBOLS} />
                    </div>
                  )}
                </div>
              </div>

              {(user || showBelowFold) && (
                <div
                  className={cn(
                    "relative z-10 flex-1",
                    !user && "animate-[fadeIn_500ms_ease-out_both]"
                  )}
                  style={!user ? { animationDelay: "40ms" } : undefined}
                >
                  <Feed limit={6} minScore={5.0} variant="teaser" />
                </div>
              )}
              {!user && showBelowFold && (
                <>
                  <div
                    className="relative z-10 animate-[fadeIn_500ms_ease-out_both]"
                    style={{ animationDelay: "90ms" }}
                  >
                    <ResearchCostSection />
                  </div>
                  <div
                    className="relative z-10 animate-[fadeIn_500ms_ease-out_both]"
                    style={{ animationDelay: "140ms" }}
                  >
                    <LogoWallSection />
                  </div>
                  <div
                    className="relative z-10 animate-[fadeIn_500ms_ease-out_both]"
                    style={{ animationDelay: "190ms" }}
                  >
                    <LovedByRetailSection />
                  </div>
                  <div
                    className="relative z-10 animate-[fadeIn_500ms_ease-out_both]"
                    style={{ animationDelay: "240ms" }}
                  >
                    <WorkflowSection />
                  </div>
                  <div
                    className="relative z-10 animate-[fadeIn_500ms_ease-out_both]"
                    style={{ animationDelay: "265ms" }}
                  >
                    <AutomationsSection />
                  </div>
                  <div
                    className="relative z-10 animate-[fadeIn_500ms_ease-out_both]"
                    style={{ animationDelay: "290ms" }}
                  >
                    <PricingSection />
                  </div>
                  <div
                    className="relative z-10 animate-[fadeIn_500ms_ease-out_both]"
                    style={{ animationDelay: "340ms" }}
                  >
                    <CTASection />
                  </div>
                  <div
                    className="relative z-10 animate-[fadeIn_500ms_ease-out_both]"
                    style={{ animationDelay: "390ms" }}
                  >
                    <Footer />
                  </div>
                </>
              )}
            </div>
          ) : (
            <>
              <Messages
                chatId={id}
                status={effectiveStatus}
                votes={votes}
                messages={messages}
                setMessages={setMessages}
                reload={reload}
                isReadonly={isReadonly}
                append={append}
                subAgentMessageIdsRef={subAgentMessageIdsRef}
                messageSkillsRef={messageSkillsRef}
                viewMode={viewMode}
                initialViewMode={initialViewMode}
                isAlertsRoute={isAlertsRoute}
                alertStartCellIndex={alertStartCellIndex}
                onSaveAsSkill={handleSaveChatAsSkill}
              />

              {showReloadChat && (
                <div className="flex justify-center my-3">
                  <button
                    type="button"
                    onClick={handleReloadChat}
                    className="rounded-lg border border-zinc-700 bg-zinc-900 px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-800 transition-colors"
                  >
                    Connection interrupted — tap to reload chat
                  </button>
                </div>
              )}

              {!isReadonly ? (
                <form className="flex mx-auto px-0 bg-background pb-4 md:pb-6 gap-2 w-full md:max-w-3xl">
                  <MultimodalInput
                    chatId={id}
                    input={input}
                    setInput={setInput}
                    handleSubmit={handleSubmitWithKeywordRouting}
                    status={effectiveStatus}
                    stop={effectiveStop}
                    attachments={attachments}
                    setAttachments={setAttachments}
                    onRemoveAttachment={removeAttachment}
                    messages={messages}
                    setMessages={setMessages}
                    append={appendWithKeywordRouting}
                    selectedVisibilityType={visibilityType}
                    dataRef={requestedDataRef}
                    deepResearchRef={deepResearchRef}
                    deepResearch={deepResearch}
                    modelTierRef={modelTierRef}
                    modelTier={modelTier}
                    onModelTierChange={handleModelTierChange}
                    selectedModelKey={selectedModelKey}
                    onModelChange={handleModelChange}
                    modelOverrideRef={modelOverrideRef}
                    activeSkillIdRef={activeSkillIdRef}
                    initialActiveSkill={
                      initialUserQuery ? undefined : initialActiveSkill
                    }
                    onDeepResearchChange={setDeepResearch}
                    onStopped={effectiveStop}
                  />
                </form>
              ) : replayId && !replayFinished && (demoReplayInputOn || replayCleanMode) ? (
                // Demo-recording mode: render a real-looking multimodal
                // input bar instead of the replay strip. Submit fires
                // one of three paths depending on state:
                //   1. Replay-gate is open (nextUserMessage truthy) →
                //      continue the replay (same callback shape the
                //      old <ReplayControls>.onContinue used at the
                //      site below — we advance the consumed-at cursor
                //      and append a user message to fire the next
                //      SSE turn). Textarea is read-only + Send button
                //      lights up to make the action obvious.
                //   2. No gate, keyword typed → swap to the matching
                //      curated replay.
                //   3. Otherwise → hand off to `/?prefill=` so the
                //      visitor can convert into a real chat.
                <DemoReplayInput
                  sessionId={id}
                  curatedId={replayId}
                  nextUserMessage={nextUserMessage}
                  // Clean mode: don't auto-prefill the canned next-user
                  // message into the textarea. The viewer types whatever
                  // they want; the backend still streams the recorded
                  // turn because /replay/stream continuation is keyed on
                  // `state.current_turn`, not on the user-message text.
                  disableAutoPrefill={replayCleanMode}
                  // Forwarded so clean-mode submit can advance the
                  // recording as soon as the previous turn finishes
                  // (status === "ready") without depending on the
                  // backend's `next_user_message` SSE frame.
                  chatStatus={effectiveStatus}
                  onContinueReplay={(msg) => {
                    replayLog("demo-input:continue-fired", {
                      msgPreview: msg.slice(0, 80),
                      dataLen: data?.length ?? 0,
                    });
                    setNextUserMessageConsumedAt(data?.length ?? 0);
                    append({ role: "user", content: msg });
                  }}
                />
              ) : replayId && !replayFinished ? (
                <ReplayControls
                  sessionId={id}
                  isStreaming={effectiveStatus === "streaming" || effectiveStatus === "submitted"}
                  nextUserMessage={nextUserMessage}
                  onContinue={(msg) => {
                    replayLog("onContinue:fired", {
                      msgPreview: msg.slice(0, 80),
                      dataLen: data?.length ?? 0,
                    });
                    setNextUserMessageConsumedAt(data?.length ?? 0);
                    append({ role: "user", content: msg });
                  }}
                  onComplete={() => {
                    replayLog("onComplete:fired", {
                      effectiveStatus,
                      nextUserMessage,
                      dataLen: data?.length ?? 0,
                      consumedAt: nextUserMessageConsumedAt,
                      dataTypesTail: (data ?? []).slice(-10).map((d: any) => d?.type ?? typeof d),
                    });
                    setReplayFinished(true);
                  }}
                  speed={replaySpeed}
                  onSpeedChange={setReplaySpeed}
                  isSkipped={replaySkipped}
                  onSkippedChange={setReplaySkipped}
                  replayQuery={title}
                  publishGate={publishGate}
                  onPublishConfirm={() => {
                    // Clear the gate on this side first so the Publish
                    // button vanishes synchronously — otherwise a slow
                    // /replay/control roundtrip would leave it hanging.
                    setPublishGateConsumedAt(data?.length ?? 0);
                  }}
                />
              ) : replayId && replayFinished && !user ? (
                <div className="flex mx-auto px-4 pb-4 md:pb-6 w-full md:max-w-3xl">
                  <PostReplayInput replayQuery={title} />
                </div>
              ) : (
                <div className="sticky bottom-0 bg-background border-t border-zinc-800 p-4">
                  <div className="flex flex-col mx-auto w-full md:max-w-3xl gap-3">
                    {readonlyReason === "breaking_change" ? (
                      <>
                        <div className="relative text-center p-4 bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800 rounded-lg border border-zinc-700 shadow-lg">
                          <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent rounded-lg pointer-events-none" />
                          <p className="relative text-base font-semibold text-foreground mb-2">
                            Hey{" "}
                            {user?.displayName?.split(" ")[0]
                              ? user.displayName
                                  .split(" ")[0]
                                  .charAt(0)
                                  .toUpperCase() +
                                user.displayName
                                  .split(" ")[0]
                                  .slice(1)
                                  .toLowerCase()
                              : "There"}{" "}
                            👋
                          </p>
                          <p className="relative text-sm text-zinc-400 leading-relaxed">
                            We launched Xynth 3.0 with major upgrades.
                            Unfortunately, breaking changes mean we can&apos;t
                            support old chats. We apologize for the
                            inconvenience but promise a much better experience!
                          </p>
                        </div>
                        <Button
                          onClick={() => router.push("/")}
                          className="w-full bg-gradient-to-r from-zinc-100 to-white hover:from-white hover:to-zinc-100 text-black font-semibold py-6 rounded-xl shadow-md hover:shadow-lg transition-all"
                        >
                          Start New Chat ✨
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => router.push("/")}
                        className="w-full bg-white text-black hover:bg-zinc-200 font-medium py-6 rounded-xl flex items-center justify-center gap-2"
                      >
                        <ArrowLeft size={20} />
                        Back
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        </div>
      )}
    </div>
  );
}
